//latest
import React, { useState, useRef, useEffect } from "react";
import { useOtherChargeOptions, applyOtherChargeToEntry } from "../utils/otherChargeOptions";
import { useLocation, useNavigate } from "react-router-dom";
import { Icon } from "@iconify/react";
import { Modal, Button, Form } from "react-bootstrap";

import ReactDOM from "react-dom";
import toast from "react-hot-toast"; // Add toast import
import { getUserData } from "./const";
import { getSessionBranchId } from "../utils/sessionBranch";
import { getActiveItems } from "../utils/statusUtils";
import { useButtonTableStyle } from "../utils/useButtonTableStyle";
import "../assets/css/theme-table-header.css";
import OtherAmountModal from "../components/OtherAmountModal";
import { hasPermission, PERMISSIONS } from "../utils/permissions";
import {
  setupPortalContainers,
  teardownPortalContainers,
} from "../utils/portalContainers";
import {
  listSuppliers,
  listMetalGroups,
  listProductGroups,
  showProducts,
  updateStock,
} from "../erpClient";
import { normalizeProductStockShowResponse } from "../utils/productStockErpAdapter";
import { extractListRowsFromResponse } from "../utils/parseApiPagination";
import { productGroupOptionDisplayName } from "../utils/productGroupPool";
import {
  mapApiChargesToOtherAmountEntries,
  mapOtherAmountEntriesForUpdate,
} from "../utils/otherAmountCalculations";

function resolveProductGroupId(rawLabel, groups) {
  if (rawLabel === undefined || rawLabel === null) return null;
  const s = String(rawLabel).trim();
  if (!s) return null;
  const asNum = Number(s);
  if (Number.isFinite(asNum) && asNum > 0 && String(asNum) === s) {
    return asNum;
  }
  if (!Array.isArray(groups) || groups.length === 0) return null;
  const lower = s.toLowerCase();
  const found = groups.find((g) => {
    const label = String(g.label ?? "").trim();
    const sc = String(g.short_code ?? "").trim().toLowerCase();
    return (
      label === s ||
      label.toLowerCase() === lower ||
      sc === lower ||
      String(g.value) === s ||
      Number(g.value) === asNum
    );
  });
  if (!found) return null;
  const id = Number(found.value);
  return Number.isFinite(id) ? id : null;
}

function resolveSupplierId(rawLabel, suppliers) {
  if (rawLabel === undefined || rawLabel === null) return null;
  const s = String(rawLabel).trim();
  if (!s) return null;
  const asNum = Number(s);
  if (Number.isFinite(asNum) && asNum > 0 && String(asNum) === s) {
    return asNum;
  }
  if (!Array.isArray(suppliers) || suppliers.length === 0) return null;
  const lower = s.toLowerCase();
  const found = suppliers.find((sup) => {
    const label = String(sup.label ?? "").trim();
    const sc = String(sup.short_code ?? "").trim().toLowerCase();
    return (
      label === s ||
      label.toLowerCase() === lower ||
      sc === lower ||
      String(sup.value) === s ||
      Number(sup.value) === asNum
    );
  });
  if (!found) return null;
  const id = Number(found.value);
  return Number.isFinite(id) ? id : null;
}

const getErpImageCandidates = (imagePath) => {
  if (!imagePath) return [];
  const raw = String(imagePath).trim();
  if (!raw) return [];
  if (/^https?:\/\//i.test(raw)) return [raw];

  const imageBase = (process.env.REACT_APP_IMAGE_BASE_URL || "").replace(/\/+$/, "");
  const normalizedPath =
    raw.includes("/") || raw.startsWith("uploads/")
      ? raw
      : `uploads/product/${raw}`;
  const cleanPath = normalizedPath.replace(/^\/+/, "");
  const fallbackPath = raw.replace(/^\/+/, "");
  if (!imageBase) return [cleanPath, fallbackPath];
  return Array.from(
    new Set([
      `${imageBase}/${cleanPath}`,
      `${imageBase}/api/${cleanPath}`,
      `${imageBase}/${fallbackPath}`,
      `${imageBase}/api/${fallbackPath}`,
    ]),
  );
};

const EditingProduct = ({ masterGoldRate, masterSilverRate }) => {
  const { buttonClass, cardClass } = useButtonTableStyle();
  const calculatePerGramRate = (rate) => {
    return rate / 10;
  };
  const location = useLocation();
  const navigate = useNavigate();
  const selectedItemIds = location.state?.selectedItemIds || [];
  const selectedIdsKey = (selectedItemIds || []).map((id) => String(id)).join(",");

  const [canEditProduct, setCanEditProduct] = useState(() =>
    hasPermission(PERMISSIONS.PRODUCT_EDIT)
  );

  useEffect(() => {
    const handlePermissionsUpdated = () => {
      setCanEditProduct(hasPermission(PERMISSIONS.PRODUCT_EDIT));
    };
    window.addEventListener("permissionsUpdated", handlePermissionsUpdated);
    window.addEventListener("erpAuthUpdated", handlePermissionsUpdated);
    return () => {
      window.removeEventListener("permissionsUpdated", handlePermissionsUpdated);
      window.removeEventListener("erpAuthUpdated", handlePermissionsUpdated);
    };
  }, []);

  // Define column structure
  const COLUMNS = [
    { id: "no", label: "No.", width: "20px", value: (row) => row.no },
    // Tag number should be visible but not editable
    { id: "tagNo", label: "Tag No.", width: "120px", value: (row) => row.tagNo },
    {
      id: "productGroup",
      label: "Product Group",
      width: "160px",
      value: (row, onChange) => (
        <input
          type="text"
          value={row.productGroup}
          onChange={(e) => onChange(row.id, "productGroup", e.target.value)}
        />
      ),
    },
    {
      id: "design",
      label: "Design",
      width: "80px",
      value: (row, onChange) => (
        <input
          type="text"
          value={row.design}
          onChange={(e) => onChange(row.id, "design", e.target.value)}
        />
      ),
    },
    {
      id: "size",
      label: "Size",
      width: "40px",
      value: (row, onChange) => (
        <input
          type="text"
          value={row.size}
          onChange={(e) => onChange(row.id, "size", e.target.value)}
        />
      ),
    },
    {
      id: "pcs",
      label: "Pcs",
      width: "50px",
      value: (row, onChange) => (
        <input
          type="number"
          value={row.pcs}
          onChange={(e) => onChange(row.id, "pcs", e.target.value)}
        />
      ),
    },
    {
      id: "grWt",
      label: "GWt",
      width: "80px",
      value: (row, onChange) => (
        <input
          type="number"
          value={row.grWt}
          onChange={(e) => onChange(row.id, "grWt", e.target.value)}
        />
      ),
    },
    {
      id: "lsWtVepari",
      label: "LWt from Vepari",
      width: "100px",
      value: (row, onChange) => (
        <input
          type="number"
          value={row.lsWtVepari}
          onChange={(e) => onChange(row.id, "lsWtVepari", e.target.value)}
        />
      ),
    },
    {
      id: "lsWtCustomer",
      label: "LWt for Customer",
      width: "100px",
      value: (row, onChange) => (
        <input
          type="number"
          value={row.lsWtCustomer}
          onChange={(e) => onChange(row.id, "lsWtCustomer", e.target.value)}
        />
      ),
    },
    {
      id: "netWt",
      label: "NWt",
      width: "80px",
      value: (row, onChange) => (
        <input
          type="number"
          value={row.netWt}
          onChange={(e) => onChange(row.id, "netWt", e.target.value)}
        />
      ),
    },
    {
      id: "lType",
      label: "Lbr Type",
      width: "40px",
      value: (row, onChange) => (
        <input
          type="text"
          value={row.lType}
          onChange={(e) => onChange(row.id, "lType", e.target.value)}
        />
      ),
    },
    {
      id: "lbrPercentage",
      label: "Lbr %",
      width: "70px",
      value: (row, onChange) => (
        <input
          type="number"
          value={row.lbrPercentage}
          onChange={(e) => onChange(row.id, "lbrPercentage", e.target.value)}
        />
      ),
    },
    {
      id: "lbrRate",
      label: "Lbr Rate",
      width: "90px",
      value: (row, onChange) => (
        <input
          type="number"
          value={row.lbrRate}
          onChange={(e) => onChange(row.id, "lbrRate", e.target.value)}
        />
      ),
    },
    {
      id: "lbrAmt",
      label: "Lbr Amt",
      width: "90px",
      value: (row, onChange) => (
        <input
          type="number"
          value={row.lbrAmt}
          onChange={(e) => onChange(row.id, "lbrAmt", e.target.value)}
        />
      ),
    },
    {
      id: "othAmt",
      label: "OthAmt",
      width: "150px",
      value: (row, onChange) => (
        <input
          type="number"
          value={row.othAmt}
          onChange={(e) => onChange(row.id, "othAmt", e.target.value)}
        />
      ),
    },
    {
      id: "mrp",
      label: "MRP",
      width: "90px",
      value: (row, onChange) => (
        <input
          type="number"
          value={row.mrp}
          onChange={(e) => onChange(row.id, "mrp", e.target.value)}
        />
      ),
    },
    {
      id: "huidNo",
      label: "HUID",
      width: "100px",
      value: (row, onChange) => (
        <div className="position-relative">
          <input
            ref={(el) => (inputRefs.current[`${row.id}-huidNo`] = el)}
            type="text"
            value={row.huidNo || ""}
            onChange={(e) => {
              onChange(row.id, "huidNo", e.target.value);
            }}
            onKeyDown={(e) => {
              handleKeyDown(e, row.id, "huidNo", "huidNo");
              handleHuidKeyDown(e, row.id);
            }}
            onDoubleClick={(e) => handleHuidInteraction(row.id, e)}
            className="form-control border-0 rounded-0"
            style={{
              fontSize: "0.85rem",
              padding: "0.4rem 0.5rem",
              backgroundColor: "transparent",
              width: "100%",
              transition: "background-color 0.2s",
            }}
            onFocus={() => handleFocus(row.id, "huidNo")}
            onBlur={(e) => {
              e.target.style.backgroundColor = "transparent";
            }}
            autoComplete="off"
            title="Double-click or press Enter twice to edit details"
          />
          <Button
            ref={(el) => (huidInfoBtnRefs.current[row.id] = el)}
            variant="link"
            className="p-0 px-1 text-primary"
            style={{
              fontSize: "1rem",
              position: "absolute",
              right: "5px",
              top: "50%",
              transform: "translateY(-50%)",
              zIndex: 1,
            }}
            onKeyDown={(e) => handleHuidInfoKeyDown(e, row.id)}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              const tooltipId = `huid-tooltip-${row.id}`;
              const tooltip = tooltipRefs.current[tooltipId];

              if (tooltip) {
                // If tooltip is already visible, hide it
                if (tooltip.style.display === "block") {
                  tooltip.style.display = "none";
                  setActiveTooltip(null);

                  // Clear any existing timeout
                  if (tooltipTimeoutRef.current) {
                    clearTimeout(tooltipTimeoutRef.current);
                    tooltipTimeoutRef.current = null;
                  }
                  return;
                }

                // Hide any other visible tooltips first
                Object.entries(tooltipRefs.current).forEach(([id, t]) => {
                  if (t && id !== tooltipId) {
                    t.style.display = "none";
                  }
                });

                // Position and show tooltip
                positionTooltip(e.currentTarget, tooltip);

                // Set this as the active tooltip
                setActiveTooltip(tooltipId);

                // Set timeout to hide after 4 seconds
                if (tooltipTimeoutRef.current) {
                  clearTimeout(tooltipTimeoutRef.current);
                }

                tooltipTimeoutRef.current = setTimeout(() => {
                  if (tooltip) {
                    tooltip.style.display = "none";
                    setActiveTooltip(null);
                    tooltipTimeoutRef.current = null;
                  }
                }, 4000);
              }
            }}
            onMouseEnter={(e) => {
              // Don't show on hover if already showing from a click
              if (tooltipTimeoutRef.current) return;

              const tooltipId = `huid-tooltip-${row.id}`;
              const tooltip = tooltipRefs.current[tooltipId];

              if (tooltip) {
                // Hide any other visible tooltips first
                Object.entries(tooltipRefs.current).forEach(([id, t]) => {
                  if (t && id !== tooltipId) {
                    t.style.display = "none";
                  }
                });

                // Position and show tooltip
                positionTooltip(e.currentTarget, tooltip);

                // Set this as the active tooltip
                setActiveTooltip(tooltipId);
              }
            }}
            onMouseLeave={(e) => {
              // Only hide if not in clicked mode
              if (tooltipTimeoutRef.current) return;

              const tooltipId = `huid-tooltip-${row.id}`;
              const currentTooltip = tooltipRefs.current[tooltipId];
              if (activeTooltip === tooltipId) {
                // Use setTimeout to allow moving to the tooltip
                setTimeout(() => {
                  // Only hide if we're not hovering over the tooltip itself
                  if (
                    activeTooltip === tooltipId &&
                    !currentTooltip?.matches(":hover") &&
                    !tooltipTimeoutRef.current
                  ) {
                    currentTooltip.style.display = "none";
                    setActiveTooltip(null);
                  }
                }, 100);
              }
            }}
          >
            <Icon icon="solar:info-circle-bold" />
          </Button>

          {/* HUID Details Tooltip */}
          <div
            ref={(el) => (tooltipRefs.current[`huid-tooltip-${row.id}`] = el)}
            className="shadow-lg rounded p-2 bg-white tooltip-container"
            style={{
              display: "none",
              width: "auto",
              maxWidth: "250px",
              zIndex: 9999,
              position: "fixed",
            }}
            onMouseEnter={() => {
              // Do nothing when hovering tooltip
              // This prevents the tooltip from disappearing
            }}
            onMouseLeave={(e) => {
              // Only hide if not in clicked mode
              if (tooltipTimeoutRef.current) return;

              const tooltipId = `huid-tooltip-${row.id}`;
              const currentTooltip = tooltipRefs.current[tooltipId];
              if (activeTooltip === tooltipId) {
                e.currentTarget.style.display = "none";
                setActiveTooltip(null);
              }
            }}
          >
            <div
              className="tooltip-arrow"
              style={{
                position: "absolute",
                width: "12px",
                height: "12px",
                backgroundColor: "white",
                border: "1px solid rgba(0,0,0,0.1)",
                transform: "rotate(45deg)",
                borderRight: "none",
                borderBottom: "none",
              }}
            />
            <div
              className="huid-details"
              style={{
                maxHeight: "200px",
                overflowY: "auto",
              }}
            >
              {row.huidNo.split("##").map((huidItem, idx) => (
                <div
                  key={idx}
                  className="py-1"
                  style={{
                    borderBottom:
                      idx !== row.huidNo.split("##").length - 1
                        ? "1px solid #eee"
                        : "none",
                  }}
                >
                  <span style={{ fontSize: "0.85rem" }}>{huidItem.trim()}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      ),
    },
    {
      id: "image",
      label: "Image",
      width: "80px",
    },
    {
      id: "supplier",
      label: "Supplier",
      width: "200px",
      value: (row, onChange) => (
        <input
          type="text"
          value={row.supplier}
          onChange={(e) => onChange(row.id, "supplier", e.target.value)}
        />
      ),
    },
  ];

  // State variables
  const [isLoading, setIsLoading] = useState(true);
  const [tableData, setTableData] = useState([]);
  const [dropdownData, setDropdownData] = useState({
    productGroup: [],
    design: [],
    supplier: [],
    lType: ["NWt", "GWt", "Pcs", "Fixed"],
  });
  const [activeDropdown, setActiveDropdown] = useState({
    row: null,
    col: null,
    type: null,
  });
  const [dropdownPosition, setDropdownPosition] = useState({
    top: 0,
    left: 0,
    width: 0,
  });
  const [highlightedIndex, setHighlightedIndex] = useState(0);
  const [showImageModal, setShowImageModal] = useState(false);
  const [previewImageInfo, setPreviewImageInfo] = useState({
    url: null,
    rowIndex: null,
    colIndex: null,
  });

  const getNextCandidate = (candidates = [], currentUrl = "") => {
    const list = Array.isArray(candidates) ? candidates : [];
    if (!list.length) return null;
    const current = String(currentUrl || "");
    const idx = list.indexOf(current);
    if (idx === -1) return list[1] || null;
    return list[idx + 1] || null;
  };

  const handleCellImageError = (rowIndex, colIndex, failedUrl) => {
    setTableData((prev) => {
      const updated = [...prev];
      const row = updated[rowIndex];
      if (!row?.data) return prev;
      const cell = row.data[colIndex];
      if (!cell) return prev;
      const nextUrl = getNextCandidate(cell.imageCandidates, failedUrl);
      if (!nextUrl || nextUrl === cell.imagePreview) return prev;
      const newRow = { ...row };
      newRow.data = [...row.data];
      newRow.data[colIndex] = { ...cell, imagePreview: nextUrl };
      updated[rowIndex] = newRow;
      return updated;
    });
  };
  const [activeTooltip, setActiveTooltip] = useState(null);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [lastSearchedProductGroupPrefix, setLastSearchedProductGroupPrefix] =
    useState("");
  const [lastSearchedSupplierPrefix, setLastSearchedSupplierPrefix] =
    useState("");
  const [productGroupApiCalled, setProductGroupApiCalled] = useState(false); // Track if API was called to show "No matching items"

  // Add state for other amount modal similar to TestingProduct.jsx
  const [otherAmountModal, setOtherAmountModal] = useState({
    isOpen: false,
    rowIndex: null,
    entries: [],
    currentEntry: {
      chargeType: "",
      pcs: "",
      ct: "",
      wt: "",
      wtPP: "",
      wtTotal: "",
      conPercentage: "",
      rateOn: "",
      rate: "",
      amount: "",
    },
    editingIndex: undefined,
  });

  // Column widths aligned with AddStock / BulkStock
  const [otherAmountColumnWidths, setOtherAmountColumnWidths] = useState({
    no: "10px",
    chargetype: "150px",
    pcs: "20px",
    ct: "50px",
    wt: "120px",
    con: "10px",
    rateon: "50px",
    rate: "68px",
    amount: "80px",
  });

  // Add rateOn options
  const rateOnOptions = ["FIXED", "PCS", "CT", "WT"];

  // Add state to track last enter press
  const [lastEnterPress, setLastEnterPress] = useState({
    time: 0,
    rowIndex: null,
  });

  // Refs
  const dropdownRef = useRef(null);
  const inputRefs = useRef({});
  const dropdownItemsRef = useRef([]);
  const fileInputRefs = useRef({});
  const tooltipRefs = useRef({});
  const tooltipTimeoutRef = useRef(null);
  const columnResizeRef = useRef(null);
  const searchTimeoutRef = useRef(null);
  const tableContainerRef = useRef(null);
  const huidInfoBtnRefs = useRef({});
  const imageBtnRefs = useRef({});

  // Columns that should have dropdown functionality
  const dropdownColumns = ["productGroup", "supplier", "lType", "rateOn"];

  // Get filtered items based on input value and column type
  const getFilteredItems = (searchText, columnType) => {
    if (!dropdownData[columnType]) return [];

    // Ensure searchText is a string
    const normalizedSearchText = searchText
      ? String(searchText).toLowerCase()
      : "";

    // Special handling for productGroup (search both label and short_code)
    if (columnType === "productGroup") {
      if (normalizedSearchText.length < 3) return [];
      return dropdownData[columnType].filter((item) => {
        const labelMatch =
          (item.label &&
            String(item.label).toLowerCase().includes(normalizedSearchText)) ||
          false;
        const shortCodeMatch =
          (item.short_code &&
            String(item.short_code)
              .toLowerCase()
              .includes(normalizedSearchText)) ||
          false;
        return labelMatch || shortCodeMatch;
      });
    }

    if (!normalizedSearchText) return dropdownData[columnType];

    // Special handling for lType (which is an array of strings)
    if (columnType === "lType") {
      return dropdownData[columnType].filter((item) =>
        item.toLowerCase().includes(normalizedSearchText)
      );
    }

    // Special handling for supplier (search both label and short_code)
    if (columnType === "supplier") {
      return dropdownData[columnType].filter((item) => {
        const labelMatch =
          item.label?.toLowerCase().includes(normalizedSearchText) || false;
        const shortCodeMatch =
          item.short_code?.toLowerCase().includes(normalizedSearchText) ||
          false;
        return labelMatch || shortCodeMatch;
      });
    }

    // For other dropdown types
    return dropdownData[columnType].filter((item) => {
      // Safely handle both object and string types
      if (typeof item === "object") {
        return item.label
          ? item.label.toLowerCase().includes(normalizedSearchText)
          : false;
      }
      return item.toLowerCase().includes(normalizedSearchText);
    });
  };

  // Handle keyboard navigation
  const handleKeyDown = (e, rowIndex, colIndex, columnType) => {
    // Handle Tab key to move to next or previous cell
    if (e.key === "Tab") {
      e.preventDefault();
      setActiveDropdown({ row: null, col: null, type: null });

      let resolvedCol = colIndex;
      if (typeof colIndex === "string") {
        const foundIdx = COLUMNS.findIndex((c) => c.id === colIndex);
        if (foundIdx >= 0) resolvedCol = foundIdx;
      }

      const isShift = e.shiftKey;
      const huidColIdx = COLUMNS.findIndex((c) => c.id === "huidNo");
      const supplierColIdx = COLUMNS.findIndex((c) => c.id === "supplier");

      // Forward Tab from HUID input -> move to HUID 'i' button
      if (!isShift && (resolvedCol === huidColIdx || colIndex === "huidNo")) {
        const iBtn = huidInfoBtnRefs.current[rowIndex];
        if (iBtn) {
          setTimeout(() => iBtn.focus(), 10);
          return;
        }
      }

      // Backward Tab (Shift+Tab) from Supplier input -> move to Image button
      if (isShift && (resolvedCol === supplierColIdx || colIndex === "supplier")) {
        const imgBtn = imageBtnRefs.current[rowIndex];
        if (imgBtn) {
          setTimeout(() => imgBtn.focus(), 10);
          return;
        }
      }

      let currRow = rowIndex;
      let currCol = resolvedCol;
      const totalRows = tableData.length;
      const totalCols = COLUMNS.length;
      let foundInput = null;

      // Search for the next/prev valid, focusable input
      while (true) {
        if (isShift) {
          currCol--;
          if (currCol < 0) {
            currCol = totalCols - 1;
            currRow--;
          }
        } else {
          currCol++;
          if (currCol >= totalCols) {
            currCol = 0;
            currRow++;
          }
        }

        if (currRow < 0 || currRow >= totalRows) {
          break;
        }

        const candidate = inputRefs.current[`${currRow}-${currCol}`];
        if (candidate && !candidate.disabled && !candidate.readOnly) {
          foundInput = candidate;
          break;
        }
      }

      if (foundInput) {
        setTimeout(() => {
          foundInput.focus();
          if (typeof foundInput.select === "function") {
            foundInput.select();
          }
        }, 10);
      }
      return;
    }

    // Show dropdown when Backspace or Delete is pressed for dropdown columns
    if (
      (e.key === "Backspace" || e.key === "Delete") &&
      isDropdownColumn(columnType)
    ) {
      // Don't prevent default here to allow character deletion

      // Show dropdown
      handleDropdownClick(rowIndex, colIndex, columnType);
      return;
    }

    // Handle arrow key navigation for dropdown
    if (
      (e.key === "ArrowDown" || e.key === "ArrowUp") &&
      isDropdownColumn(columnType)
    ) {
      e.preventDefault();

      // Show dropdown if not already visible
      if (
        activeDropdown.row !== rowIndex ||
        activeDropdown.col !== colIndex ||
        activeDropdown.type !== columnType
      ) {
        handleDropdownClick(rowIndex, colIndex, columnType);
      }

      const items = getFilteredItems(
        tableData[rowIndex].data[colIndex].value,
        columnType
      );

      // Update highlighted index
      setHighlightedIndex((prevIndex) => {
        if (e.key === "ArrowDown") {
          return Math.min(prevIndex + 1, items.length - 1);
        } else {
          return Math.max(prevIndex - 1, 0);
        }
      });
      return;
    }

    // Select highlighted item with Enter
    if (
      e.key === "Enter" &&
      activeDropdown.row === rowIndex &&
      activeDropdown.col === colIndex
    ) {
      e.preventDefault();

      const items = getFilteredItems(
        tableData[rowIndex].data[colIndex].value,
        columnType
      );

      if (items.length > 0 && highlightedIndex < items.length) {
        handleDropdownSelect(rowIndex, colIndex, items[highlightedIndex]);
      } else if (isDropdownColumn(columnType)) {
        // If no items are filtered but it's a dropdown column, show the dropdown
        handleDropdownClick(rowIndex, colIndex, columnType);
      }
      return;
    }

    // Handle space key to open dropdown ONLY if dropdown isn't already open
    if (e.key === " " && isDropdownColumn(columnType)) {
      // Check if dropdown is already open for this cell
      if (
        activeDropdown.row !== rowIndex ||
        activeDropdown.col !== colIndex ||
        activeDropdown.type !== columnType
      ) {
        // Dropdown is not open, prevent default and open it
        e.preventDefault();
        handleDropdownClick(rowIndex, colIndex, columnType);
      }
      // If dropdown is already open, allow the space character to be entered
      // by not preventing the default action
    }
  };

  const handleHuidInfoKeyDown = (e, rowIndex) => {
    if (e.key === "Tab") {
      e.preventDefault();
      if (e.shiftKey) {
        // Shift+Tab -> go back to HUID input
        const huidCol = COLUMNS.findIndex((c) => c.id === "huidNo");
        const huidInput = inputRefs.current[`${rowIndex}-${huidCol}`];
        if (huidInput) {
          setTimeout(() => {
            huidInput.focus();
            if (typeof huidInput.select === "function") huidInput.select();
          }, 10);
        }
      } else {
        // Tab -> go to Image button
        const imgBtn = imageBtnRefs.current[rowIndex];
        if (imgBtn) {
          setTimeout(() => imgBtn.focus(), 10);
        } else {
          const supplierCol = COLUMNS.findIndex((c) => c.id === "supplier");
          const supplierInput = inputRefs.current[`${rowIndex}-${supplierCol}`];
          if (supplierInput) {
            setTimeout(() => {
              supplierInput.focus();
              if (typeof supplierInput.select === "function") supplierInput.select();
            }, 10);
          }
        }
      }
    }
  };

  const handleImageKeyDown = (e, rowIndex, colIndex) => {
    if (e.key === "Tab") {
      e.preventDefault();
      if (e.shiftKey) {
        // Shift+Tab -> go to HUID 'i' button
        const iBtn = huidInfoBtnRefs.current[rowIndex];
        if (iBtn) {
          setTimeout(() => iBtn.focus(), 10);
        } else {
          const huidCol = COLUMNS.findIndex((c) => c.id === "huidNo");
          const huidInput = inputRefs.current[`${rowIndex}-${huidCol}`];
          if (huidInput) {
            setTimeout(() => {
              huidInput.focus();
              if (typeof huidInput.select === "function") huidInput.select();
            }, 10);
          }
        }
      } else {
        // Tab -> go to Supplier input
        const supplierCol = COLUMNS.findIndex((c) => c.id === "supplier");
        const supplierInput = inputRefs.current[`${rowIndex}-${supplierCol}`];
        if (supplierInput) {
          setTimeout(() => {
            supplierInput.focus();
            if (typeof supplierInput.select === "function") supplierInput.select();
          }, 10);
        }
      }
    }
  };

  // Add this effect to scroll dropdown item into view when highlighted index changes
  useEffect(() => {
    if (
      activeDropdown.row !== null &&
      activeDropdown.col !== null &&
      activeDropdown.type &&
      dropdownItemsRef.current[highlightedIndex]
    ) {
      const highlightedItem = dropdownItemsRef.current[highlightedIndex];
      highlightedItem.scrollIntoView({
        block: "nearest",
        inline: "nearest",
      });
    }
  }, [highlightedIndex, activeDropdown]);

  // Lazy fetch product groups only when the user searches in the cell (not on page load)
  const fetchProductGroupsByPrefix = async (prefix = "") => {
    try {
      const response = await listProductGroups();
      if (response?.success && response.data) {
        let productGroups = [];
        const metalGroupsResponse = await listMetalGroups();
        // console.log(
        //   "Metal Groups node API Response:",
        //   metalGroupsResponse.data
        // );

        // Create a map of metal group IDs to their purity
        const metalGroupsMap = {};
        const mgList =
          metalGroupsResponse?.success &&
          Array.isArray(metalGroupsResponse.data?.metal_groups)
            ? metalGroupsResponse.data.metal_groups
            : metalGroupsResponse?.success &&
                Array.isArray(metalGroupsResponse.data?.records)
              ? metalGroupsResponse.data.records
              : [];

        mgList.forEach((group) => {
          const purity = group.purity ?? group.Purity ?? "";
          const metalRaw = group.metal ?? group.Metal ?? "";
          const metalNorm =
            typeof metalRaw === "string" && metalRaw.toLowerCase() === "gold"
              ? "Gold"
              : typeof metalRaw === "string" &&
                metalRaw.toLowerCase() === "silver"
              ? "Silver"
              : typeof metalRaw === "string" &&
                metalRaw.toLowerCase() === "platinum"
              ? "Platinum"
              : metalRaw;

          metalGroupsMap[group.id] = {
            purity: purity,
            metal: metalNorm,
          };
        });

        const pgList = Array.isArray(response.data.product_groups)
          ? response.data.product_groups
          : [];
        const filteredPgList =
          prefix && prefix.length >= 3
            ? pgList.filter((g) =>
                String(g.name || "")
                  .toLowerCase()
                  .includes(String(prefix).toLowerCase()),
              )
            : pgList;

        if (Array.isArray(filteredPgList)) {
          productGroups = filteredPgList.map((group) => {
            // Extract metal type from product group label
            let metalType = "";
            const label = String(group.name || "").toLowerCase();
            if (label.includes("gold")) {
              metalType = "Gold";
            } else if (label.includes("silver")) {
              metalType = "Silver";
            } else if (label.includes("platinum")) {
              metalType = "Platinum";
            } else if (metalGroupsMap[group.metal_group_id]?.metal) {
              metalType = metalGroupsMap[group.metal_group_id].metal;
            }

            return {
              value: group.id,
              label: group.name || "",
              short_code: group.short_code || "",
              purity: metalGroupsMap[group.metal_group_id]?.purity || "",
              metal: metalType,
            };
          });
        }

        // Remove duplicates
        const uniqueProductGroups = Array.from(
          new Map(productGroups.map((item) => [item.value, item])).values()
        );

        // Update dropdown data with fetched product group options
        setDropdownData((prev) => ({
          ...prev,
          productGroup: uniqueProductGroups,
        }));

        if (prefix && prefix.length >= 3) {
          setLastSearchedProductGroupPrefix(prefix);
          setProductGroupApiCalled(true); // Mark that API was called
        }
      } else {
        // If no product groups found or error message
        setDropdownData((prev) => ({
          ...prev,
          productGroup: [],
        }));
        if (prefix && prefix.length >= 3) {
          setProductGroupApiCalled(true); // Mark that API was called (even if no results)
        }
      }
    } catch (error) {
      console.error("Error fetching product group options:", error);
      // On error, clear the dropdown
      setDropdownData((prev) => ({
        ...prev,
        productGroup: [],
      }));
      if (prefix && prefix.length >= 3) {
        setProductGroupApiCalled(true); // Mark that API was called (even on error)
      }
    }
  };

  const fetchSuppliers = async (prefix = "") => {
    if (prefix && prefix.length >= 2 && lastSearchedSupplierPrefix === prefix) {
      return;
    }

    try {
      if (prefix && prefix.length >= 2) {
        setLastSearchedSupplierPrefix(prefix);
      }

      const response = await listSuppliers({
        search: prefix && prefix.length >= 2 ? prefix : undefined,
      });
      const suppliersList = extractListRowsFromResponse(response, ["suppliers"]);

      if (suppliersList.length > 0) {
        const activeSuppliers = getActiveItems(suppliersList, {
          activeValue: 1,
        });

        const fetchedSuppliers = activeSuppliers.map((supplier) => ({
          value: supplier.id,
          label: supplier.company_name || supplier.name || "",
          short_code: supplier.short_code || "",
        }));

        setDropdownData((prev) => ({
          ...prev,
          supplier: fetchedSuppliers,
        }));
      } else {
        setDropdownData((prev) => ({
          ...prev,
          supplier: [],
        }));
      }
    } catch (error) {
      setDropdownData((prev) => ({
        ...prev,
        supplier: [],
      }));
    }
  };

  const productGroupLabelFromProduct = (product) => {
    if (!product) return "";
    if (product.product_group_name) return String(product.product_group_name);
    if (product.product_group && typeof product.product_group === "object") {
      return String(product.product_group.name || product.product_group.label || "");
    }
    if (typeof product.product_group === "string" && Number.isNaN(Number(product.product_group))) {
      return product.product_group;
    }
    return "";
  };

  const supplierLabelFromProduct = (product) => {
    if (!product) return "";
    return (
      product.supplier_company_name ||
      product.supplier_name ||
      product.supplier?.company_name ||
      product.supplier?.name ||
      product.karigar_company_name ||
      ""
    );
  };

  const inferMetalFromLabel = (label) => {
    const s = String(label || "").toLowerCase();
    if (s.includes("gold")) return "Gold";
    if (s.includes("silver")) return "Silver";
    if (s.includes("platinum")) return "Platinum";
    return "";
  };

  const fetchSelectedItems = async () => {
    const normalizedIds = (selectedItemIds || [])
      .map((id) => String(id))
      .filter((id) => id && id !== "undefined" && id !== "null");

    if (normalizedIds.length === 0) {
      setTableData([]);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    try {
      const response = await showProducts({ id: normalizedIds });
      console.log("[EditStock] showProducts response", response);

      let allProducts = normalizeProductStockShowResponse(response);

      // Batch show may return a single product or partial list — fetch missing IDs individually.
      if (allProducts.length < normalizedIds.length) {
        const foundIds = new Set(
          allProducts.map((p) => String(p?.id ?? p?.product_id)).filter(Boolean),
        );
        const missingIds = normalizedIds.filter((id) => !foundIds.has(id));
        if (missingIds.length > 0) {
          const individualResults = await Promise.all(
            missingIds.map(async (id) => {
              try {
                const res = await showProducts({ id });
                return normalizeProductStockShowResponse(res)[0] || null;
              } catch {
                return null;
              }
            }),
          );
          allProducts = [
            ...allProducts,
            ...individualResults.filter(Boolean),
          ];
        }
      }

      if (allProducts.length === 0) {
        console.warn("No products found in response");
        setTableData([]);
        return;
      }

      const productById = new Map();
      allProducts.forEach((p) => {
        const productId = p?.id ?? p?.product_id;
        if (productId != null && productId !== "") {
          productById.set(String(productId), p);
        }
      });

      const selectedProducts = normalizedIds
        .map((id) => productById.get(id))
        .filter(Boolean);

      if (selectedProducts.length === 0) {
        console.warn("No matching products found for selected IDs");
        setTableData([]);
        return;
      }

      // Seed dropdown options from product payload (ids + names already on product API)
      const pgById = new Map();
      const supplierById = new Map();
      selectedProducts.forEach((product) => {
        const pgId =
          product.product_group_id ??
          product.product_group?.id ??
          (typeof product.product_group === "number" ||
          (typeof product.product_group === "string" &&
            String(Number(product.product_group)) === String(product.product_group).trim())
            ? product.product_group
            : null);
        const pgLabel = productGroupLabelFromProduct(product);
        if (pgId != null && pgId !== "") {
          pgById.set(String(pgId), {
            value: Number(pgId) || pgId,
            label: pgLabel || String(pgId),
            short_code:
              product.product_group_short_code ||
              product.product_group?.short_code ||
              "",
            purity: product.purity || product.product_group?.purity || "",
            metal:
              product.metal ||
              product.metal_type ||
              product.product_group?.metal ||
              inferMetalFromLabel(pgLabel) ||
              "Gold",
          });
        }

        const sid =
          product.supplier_id ??
          product.Supplier ??
          product.supplier?.id ??
          null;
        const sLabel = supplierLabelFromProduct(product);
        if (sid != null && sid !== "") {
          supplierById.set(String(sid), {
            value: Number(sid) || sid,
            label: sLabel || String(sid),
            short_code:
              product.supplier_short_code || product.supplier?.short_code || "",
          });
        }
      });
      setDropdownData((prev) => ({
        ...prev,
        productGroup: Array.from(pgById.values()),
        supplier: Array.from(supplierById.values()),
      }));

      const productRows = selectedProducts.map((product, index) => {
          const productGroupId =
            product.product_group_id ??
            product.product_group?.id ??
            (typeof product.product_group === "number" ||
            (typeof product.product_group === "string" &&
              String(Number(product.product_group)) ===
                String(product.product_group).trim())
              ? product.product_group
              : undefined);
          const supplierId =
            product.supplier_id ??
            product.Supplier ??
            product.supplier?.id ??
            undefined;

          const rowData = COLUMNS.map((column) => {
            let value = "";
            let imageFile = null;
            let imagePreview = null;
            let otherAmountEntries = [];

            switch (column.id) {
              case "no":
                value = (index + 1).toString();
                break;
              case "tagNo":
                value = product.tag_no || product.tagNo || "";
                break;
              case "productGroup":
                value = productGroupLabelFromProduct(product);
                break;
              case "design":
                value = product.design || "";
                break;
              case "size":
                value = product.size || "";
                break;
              case "pcs":
                value = product.current_pcs ?? product.pcs ?? "";
                break;
              case "grWt":
                value = product.gwt ?? product.current_gwt ?? product.GWT ?? "";
                break;
              case "lsWtVepari":
                value =
                  product.lwt_vepari ??
                  product.LWt_vepari ??
                  product.LWT_vepari ??
                  product.LWT ??
                  "";
                break;
              case "lsWtCustomer":
                value =
                  product.lwt_customer ??
                  product.LWt_customer ??
                  product.LWT_customer ??
                  "";
                break;
              case "netWt":
                value = product.nwt ?? product.NWT ?? "";
                break;
              case "lType":
                value = product.lbr_type ?? product.LBR_type ?? "";
                break;
              case "lbrPercentage":
                value = product.lbr_percent ?? product.LBR ?? "";
                break;
              case "lbrRate":
                value = product.lbr_rate ?? product.LBR_rate ?? "";
                break;
              case "lbrAmt":
                value = product.lbr_amt ?? product.LBR_amt ?? "";
                break;
              case "othAmt":
                value = product.oth_amt ?? product.othamt ?? "";
                if (product.charges && Array.isArray(product.charges)) {
                  otherAmountEntries = mapApiChargesToOtherAmountEntries(
                    product.charges,
                  );
                  const parsedOth = Number(value || 0);
                  const chargesTotal = otherAmountEntries.reduce(
                    (sum, c) => sum + (Number(c.amount) || 0),
                    0,
                  );
                  if ((!parsedOth || parsedOth === 0) && chargesTotal > 0) {
                    value = String(chargesTotal);
                  }
                }
                break;
              case "mrp":
                value = product.mrp ?? product.MRP ?? "";
                break;
              case "image":
                value = product.image || "";
                {
                  const candidates = getErpImageCandidates(product.image);
                  imagePreview = candidates[0] || "";
                }
                break;
              case "supplier":
                value = supplierLabelFromProduct(product);
                break;
              case "huidNo":
                value = product.huid || "";
                break;
              default:
                value = "";
            }

            return {
              id: column.id,
              value,
              ...(column.id === "supplier" && supplierId != null
                ? { supplierId }
                : {}),
              ...(column.id === "productGroup" && productGroupId != null
                ? { productGroupId }
                : {}),
              ...(column.id === "image"
                ? {
                    imageFile,
                    imagePreview,
                    imageCandidates: getErpImageCandidates(value),
                  }
                : {}),
              ...(column.id === "othAmt" ? { otherAmountEntries } : {}),
            };
          });

          return {
            id: product.id ?? product.product_id,
            productGroupId: productGroupId ?? "",
            supplierId,
            data: rowData,
          };
        });

      setTableData(productRows);
    } catch (error) {
      console.error("Error fetching selected items:", error);
      toast.error("Failed to load products for editing");
      setTableData([]);
    } finally {
      setIsLoading(false);
    }
  };

  // Create modal portal container for other amount modal
  // Listen for API URL changes - navigate back to table
  useEffect(() => {
    const handleApiUrlChange = (event) => {
      console.log('API URL changed in EditStock, navigating back...', event.detail);
      toast('Database switched. Returning to stock table.');
      navigate('/stocktable');
    };

    window.addEventListener('apiUrlChanged', handleApiUrlChange);
    
    return () => {
      window.removeEventListener('apiUrlChanged', handleApiUrlChange);
    };
  }, [navigate]);

  useEffect(() => {
    setupPortalContainers();
    return teardownPortalContainers;
  }, []);

  // Page load: product API only — product_group & supplier names/ids come with each product
  useEffect(() => {
    if (!selectedIdsKey) {
      setTableData([]);
      setIsLoading(false);
      return;
    }
    fetchSelectedItems();
  }, [selectedIdsKey]);

  useEffect(() => {
    const handleProductsRestored = (event) => {
      const restored = event?.detail?.restored_product_ids || [];
      const hasMatch =
        restored.length === 0 ||
        (selectedItemIds || []).some((id) =>
          restored.some((rid) => String(rid) === String(id)),
        );
      if (hasMatch && selectedIdsKey) {
        fetchSelectedItems();
      }
    };
    window.addEventListener("erp:products-restored", handleProductsRestored);
    return () => {
      window.removeEventListener(
        "erp:products-restored",
        handleProductsRestored,
      );
    };
  }, [selectedIdsKey, selectedItemIds]);

  // Update dropdown position after API response when dropdown is active
  useEffect(() => {
    if (
      productGroupApiCalled &&
      activeDropdown.type === "productGroup" &&
      activeDropdown.row !== null &&
      activeDropdown.col !== null
    ) {
      setTimeout(() => {
        const inputElement =
          inputRefs.current[`${activeDropdown.row}-${activeDropdown.col}`];
        if (inputElement) {
          const rect = inputElement.getBoundingClientRect();
          setDropdownPosition({
            top: rect.bottom + window.scrollY,
            left: rect.left + window.scrollX,
            width: rect.width,
          });
        }
      }, 10);
    }
  }, [productGroupApiCalled, dropdownData.productGroup, activeDropdown]);

  // Utility functions
  const calculateNetWeight = (gw, lw) => {
    return gw - lw; // NWt = GWt - LWt
  };

  // Function to calculate Labor Amount using Lbr %
  const calculateLbrAmtWithPercentage = (
    baseWeight,
    lbrPercentage,
    masterRate
  ) => {
    return (
      (lbrPercentage / 100) * baseWeight * calculatePerGramRate(masterRate)
    );
  };

  // Function to calculate Labor Amount using Lbr Rate
  const calculateLbrAmtWithRate = (baseWeight, lbrRate) => {
    return lbrRate * baseWeight;
  };

  const calculateLbrAmt = (baseWeight, lbrPercentage, lbrRate, metalType) => {
    let lbrAmt = 0;
    if (parseFloat(lbrPercentage) > 0) {
      const masterRate =
        metalType === "Gold" ? masterGoldRate : masterSilverRate;
      lbrAmt = calculateLbrAmtWithPercentage(
        baseWeight,
        lbrPercentage,
        masterRate
      );
    } else if (parseFloat(lbrRate) > 0) {
      lbrAmt = calculateLbrAmtWithRate(baseWeight, lbrRate);
    }
    return parseFloat(lbrAmt) || 0;
  };

  // Add calculation functions from TestingProduct.jsx
  const calculateTotalCt = (entry) => {
    const ct = parseFloat(entry.ct);

    if (isNaN(ct)) return 0;
    return ct;
  };

  /** Total weight for "WT" rate: explicit Total (wtTotal) XOR per-piece (wtPP/wt) × Pcs. */
  const calculateTotalWt = (entry) => {
    const tStr = entry.wtTotal;
    const hasTotal =
      tStr !== undefined &&
      tStr !== null &&
      String(tStr).trim() !== "" &&
      Number.isFinite(parseFloat(tStr));

    if (hasTotal) {
      const t = parseFloat(tStr);
      return Number.isFinite(t) ? t : 0;
    }

    const pcs = parseFloat(entry.pcs);
    const wt = parseFloat(entry.wtPP ?? entry.wt);
    if (isNaN(pcs) || isNaN(wt)) return 0;
    return pcs * wt;
  };

  const calculateLessWeight = (entry) => {
    // Calculate total weight first
    const totalWt = calculateTotalWt(entry);
    if (!totalWt || isNaN(totalWt)) return 0;

    // Get concentration percentage
    const conPercentage = parseFloat(entry.conPercentage);

    // If there's a valid concentration percentage, calculate less weight based on that
    if (!isNaN(conPercentage) && conPercentage > 0) {
      return totalWt * (conPercentage / 100);
    }

    // If no concentration percentage, return the total weight
    return totalWt;
  };

  const calculateAmount = (entry) => {
    if (!entry.rate || isNaN(entry.rate)) return 0;

    const rate = parseFloat(entry.rate);
    const rateOn = entry.rateOn?.toLowerCase() || "";
    let amount = 0;

    switch (rateOn) {
      case "fixed":
        amount = rate;
        break;
      case "pcs":
        amount =
          entry.pcs && !isNaN(entry.pcs) ? rate * parseFloat(entry.pcs) : 0;
        break;
      case "ct":
        const totalCt = calculateTotalCt(entry);
        amount = totalCt ? rate * totalCt : 0;
        break;
      case "wt":
        const totalWt = calculateTotalWt(entry);
        amount = totalWt ? rate * totalWt : 0;
        break;
      default:
        amount = 0;
    }

    return amount;
  };

  const calculateTotal = (entries) => {
    return entries.reduce(
      (sum, entry) => sum + (parseFloat(entry.amount) || 0),
      0,
    );
  };

  const formatAmount = (value) => {
    if (!value || isNaN(value)) return "";
    // Convert to number and handle rounding based on decimal part
    const num = parseFloat(value);
    const decimalPart = num - Math.floor(num);
    // If decimal part is >= 0.5, round up, otherwise round down
    return decimalPart >= 0.5
      ? Math.ceil(num).toString()
      : Math.floor(num).toString();
  };

  const formatLessWeight = (value) => {
    // Convert to number first
    const num = parseFloat(value);
    // Check if it's a valid number (including 0)
    if (isNaN(num)) return "0.00";
    // Always return with 2 decimal places
    return num.toFixed(2);
  };

  // Add Other Amount Modal functions from TestingProduct.jsx
  const positionTooltip = (targetElement, tooltip) => {
    if (!targetElement || !tooltip) return;

    const rect = targetElement.getBoundingClientRect();
    tooltip.style.display = "block";

    // Get arrow element
    const arrow = tooltip.querySelector(".tooltip-arrow");

    // Responsive positioning based on viewport
    const viewportWidth = window.innerWidth;

    if (viewportWidth < 576) {
      // Small screens (Bootstrap's sm breakpoint)
      // Position directly below the "i" button
      tooltip.style.left = `${rect.left + rect.width / 2 - 100}px`; // Center horizontally (tooltip width is 200px)
      tooltip.style.top = `${rect.bottom + 10}px`; // Position below the button
      tooltip.style.maxWidth = "calc(100vw - 20px)"; // 10px padding on each side

      // Position arrow at top center
      if (arrow) {
        arrow.style.top = "-6px"; // Adjusted for better alignment
        arrow.style.left = "50%";
        arrow.style.transform = "translateX(-50%) rotate(45deg)";
        arrow.style.borderRight = "none";
        arrow.style.borderBottom = "none";
        arrow.style.borderLeft = "1px solid rgba(0,0,0,0.1)";
        arrow.style.borderTop = "1px solid rgba(0,0,0,0.1)";
      }
    } else {
      // Position to the right on larger screens
      tooltip.style.left = `${rect.right + 10}px`;
      tooltip.style.top = `${rect.top - 5}px`;
      tooltip.style.maxWidth = "300px";

      // Position arrow at left middle
      if (arrow) {
        arrow.style.top = "15px";
        arrow.style.left = "-6px";
        arrow.style.transform = "rotate(45deg)";
        arrow.style.borderRight = "none";
        arrow.style.borderBottom = "none";
      }
    }
  };

  const handleOtherAmountInteraction = (rowIndex, event) => {
    if (event.type === "dblclick") {
      event.preventDefault();
      openOtherAmountModal(rowIndex);
    } else if (event.type === "keydown" && event.key === "Enter") {
      const now = Date.now();
      if (
        lastEnterPress.rowIndex === rowIndex &&
        now - lastEnterPress.time < 500
      ) {
        // 500ms threshold for double-Enter
        event.preventDefault();
        openOtherAmountModal(rowIndex);
        setLastEnterPress({ time: 0, rowIndex: null });
      } else {
        setLastEnterPress({ time: now, rowIndex });
      }
    } else if (event.type === "click") {
      // Show tooltip for 4 seconds on click
      const tooltipId = `oth-amt-tooltip-${rowIndex}`;
      const tooltip = tooltipRefs.current[tooltipId];

      if (tooltip) {
        // Clear any existing timeout
        if (tooltipTimeoutRef.current) {
          clearTimeout(tooltipTimeoutRef.current);
        }

        // Check if tooltip is already visible
        if (tooltip.style.display === "block") {
          // Reset the timeout to keep it visible
          tooltipTimeoutRef.current = setTimeout(() => {
            tooltip.style.display = "none";
            setActiveTooltip(null);
          }, 4000);
          return; // Exit to prevent further actions
        }

        tooltip.style.display = "block"; // Show tooltip immediately
        setActiveTooltip(tooltipId); // Set active tooltip

        // Set timeout to hide after 4 seconds
        tooltipTimeoutRef.current = setTimeout(() => {
          tooltip.style.display = "none";
          setActiveTooltip(null);
        }, 4000);
      }
    }
  };

  const handleHuidInteraction = (rowIndex, event) => {
    if (event.type === "dblclick") {
      event.preventDefault();
      openHuidModal(rowIndex);
    } else if (event.type === "keydown" && event.key === "Enter") {
      const now = Date.now();
      if (
        lastHuidEnterPress.rowIndex === rowIndex &&
        now - lastHuidEnterPress.time < 500
      ) {
        // 500ms threshold for double-Enter
        event.preventDefault();
        openHuidModal(rowIndex);
        setLastHuidEnterPress({ time: 0, rowIndex: null });
      } else {
        setLastHuidEnterPress({ time: now, rowIndex });
      }
    } else if (event.type === "click") {
      // Show tooltip for 4 seconds on click
      const tooltipId = `huid-tooltip-${rowIndex}`;
      const tooltip = tooltipRefs.current[tooltipId];

      if (tooltip) {
        // Clear any existing timeout
        if (tooltipTimeoutRef.current) {
          clearTimeout(tooltipTimeoutRef.current);
        }

        // Check if tooltip is already visible
        if (tooltip.style.display === "block") {
          // Reset the timeout to keep it visible
          tooltipTimeoutRef.current = setTimeout(() => {
            tooltip.style.display = "none";
            setActiveTooltip(null);
          }, 4000);
          return; // Exit to prevent further actions
        }

        tooltip.style.display = "block"; // Show tooltip immediately
        setActiveTooltip(tooltipId); // Set active tooltip

        // Set timeout to hide after 4 seconds
        tooltipTimeoutRef.current = setTimeout(() => {
          tooltip.style.display = "none";
          setActiveTooltip(null);
        }, 4000);
      }
    }
  };

  const openOtherAmountModal = (rowIndex) => {
    const currentEntries =
      tableData[rowIndex].data.find((cell) => cell.id === "othAmt")
        .otherAmountEntries || [];
    const currentValue =
      tableData[rowIndex].data.find((cell) => cell.id === "othAmt").value || "";

    // If there's a direct value but no entries, create an initial entry
    const initialEntries =
      currentEntries.length > 0
        ? currentEntries.map((entry) => {
            const wtPPRaw = entry.wtPP ?? "";
            const wtTotalRaw = entry.wtTotal ?? "";
            const legacyWt = entry.wt ?? "";
            // Prefer explicit fields; only use legacy `wt` as PP when neither Total nor PP is set.
            const hasPP = String(wtPPRaw).trim() !== "";
            const hasTotal = String(wtTotalRaw).trim() !== "";
            const wtPP =
              hasPP ? wtPPRaw : !hasTotal ? legacyWt : "";
            return {
              ...entry,
              rateOn: entry.rateOn || "",
              wtPP,
              wtTotal: wtTotalRaw,
              wt: wtPP, // internal calc uses PP in `wt` when Total is empty
            };
          })
        : currentValue
          ? [
              {
                chargeType: "Direct Entry",
                pcs: "",
                ct: "",
                wt: "",
                wtPP: "",
                wtTotal: "",
                conPercentage: "",
                rateOn: "",
                rate: "",
                amount: currentValue,
              },
            ]
          : [];

    setOtherAmountModal({
      isOpen: true,
      rowIndex,
      entries: [...initialEntries],
      currentEntry: {
        chargeType: "",
        pcs: "",
        ct: "",
        wt: "",
        wtPP: "",
        wtTotal: "",
        conPercentage: "",
        rateOn: "",
        rate: "",
        amount: "",
      },
      editingIndex: undefined,
    });
  };

  const handleSaveOtherAmountEntries = () => {
    const { rowIndex, entries, currentEntry } = otherAmountModal;

    let finalEntries = [...entries];
    if (currentEntry.amount && currentEntry.chargeType) {
      if (isNaN(currentEntry.amount)) {
        alert("Please enter a valid amount");
        return;
      }
      finalEntries.push({ ...currentEntry });
    }

    const totalAmount = finalEntries.reduce(
      (sum, entry) => sum + (parseFloat(entry.amount) || 0),
      0,
    );

    const totalLessWeight = finalEntries.reduce((sum, entry) => {
      const lessWeight = calculateLessWeight(entry);
      return sum + (isNaN(lessWeight) ? 0 : lessWeight);
    }, 0);

    setTableData((prev) => {
      const newData = [...prev];
      const row = newData[rowIndex];

      if (!row || !row.data) return prev;

      const lTypeCell = row.data.find((cell) => cell.id === "lType");
      const lbrAmtCell = row.data.find((cell) => cell.id === "lbrAmt");
      const currentLbrAmt = lbrAmtCell?.value || "0";

      const othAmtCell = row.data.find((cell) => cell.id === "othAmt");
      if (othAmtCell) {
        othAmtCell.value = formatAmount(totalAmount);
        othAmtCell.otherAmountEntries = finalEntries;
      }

      // Sync LWt for Customer from Other Amount entries (same as Add Stock)
      const lsWtCustomerCell = row.data.find(
        (cell) => cell.id === "lsWtCustomer",
      );
      if (lsWtCustomerCell) {
        lsWtCustomerCell.value = formatLessWeight(totalLessWeight);
      }

      const gwValue = parseFloat(
        row.data.find((cell) => cell.id === "grWt")?.value || 0,
      );
      const lwValue = parseFloat(
        row.data.find((cell) => cell.id === "lsWtCustomer")?.value || 0,
      );
      const netWeight = calculateNetWeight(gwValue, lwValue);
      const netWtCell = row.data.find((cell) => cell.id === "netWt");
      if (netWtCell) {
        netWtCell.value = netWeight.toFixed(2);
      }

      const lbrType = lTypeCell?.value;
      const isLbrTypeFixed =
        String(lbrType || "")
          .trim()
          .toLowerCase() === "fixed";
      if (!isLbrTypeFixed) {
        const lbrPercentage =
          parseFloat(
            row.data.find((cell) => cell.id === "lbrPercentage")?.value,
          ) || 0;
        const lbrRate =
          parseFloat(row.data.find((cell) => cell.id === "lbrRate")?.value) ||
          0;
        const baseWeight = lbrType === "NWt" ? netWeight : gwValue;

        const productGroupCell = row.data.find(
          (cell) => cell.id === "productGroup",
        );
        const selectedProductGroup = dropdownData.productGroup.find(
          (pg) => pg.label === productGroupCell?.value,
        );
        const metalType = selectedProductGroup?.metal || "Gold";

        const lbrAmt = calculateLbrAmt(
          baseWeight,
          lbrPercentage,
          lbrRate,
          metalType,
        );
        if (lbrAmtCell) {
          lbrAmtCell.value = parseFloat(lbrAmt).toFixed(2);
        }
      } else if (lbrAmtCell) {
        lbrAmtCell.value = currentLbrAmt;
      }

      return newData;
    });

    setOtherAmountModal((prev) => ({
      ...prev,
      isOpen: false,
    }));
  };

  const handleOtherAmountColumnWidthChange = (column, newWidth) => {
    setOtherAmountColumnWidths((prev) => ({
      ...prev,
      [column]: `${newWidth}px`,
    }));
  };

  const handleRateOnDropdown = (rowIndex, value, isCurrentEntry = false) => {
    if (isCurrentEntry) {
      setOtherAmountModal((prev) => {
        const updatedEntry = {
          ...prev.currentEntry,
          rateOn: value,
        };
        // Calculate amount when rate on changes
        if (updatedEntry.rate) {
          updatedEntry.amount = formatAmount(calculateAmount(updatedEntry));
        }
        return {
          ...prev,
          currentEntry: updatedEntry,
        };
      });
    } else {
      setOtherAmountModal((prev) => {
        const newEntries = [...prev.entries];
        const updatedEntry = {
          ...newEntries[rowIndex],
          rateOn: value,
        };
        // Calculate amount when rate on changes
        if (updatedEntry.rate) {
          updatedEntry.amount = formatAmount(calculateAmount(updatedEntry));
        }
        newEntries[rowIndex] = updatedEntry;
        return {
          ...prev,
          entries: newEntries,
        };
      });
    }

    // Hide the dropdown after selection
    const dropdowns = document.querySelectorAll(".dropdown-menu");
    dropdowns.forEach((dropdown) => {
      if (dropdown.style.display === "block") {
        dropdown.style.display = "none";
      }
    });
  };

  const handleRateOnKeyDown = (e, rowIndex, isCurrentEntry = false) => {
    const currentValue = isCurrentEntry
      ? otherAmountModal.currentEntry.rateOn
      : otherAmountModal.entries[rowIndex].rateOn;

    // Always filter results, but include all if empty
    const filtered = currentValue
      ? rateOnOptions.filter((option) =>
          option.toLowerCase().includes((currentValue || "").toLowerCase())
        )
      : rateOnOptions;

    const dropdown = e.target.nextElementSibling;

    if (e.key === "Enter") {
      e.preventDefault();

      // Check if dropdown is visible
      if (
        dropdown &&
        dropdown.style.display === "block" &&
        filtered.length > 0
      ) {
        // Select the highlighted item
        handleRateOnDropdown(
          rowIndex,
          filtered[highlightedIndex],
          isCurrentEntry
        );
      } else if (dropdown) {
        // Show dropdown if not visible
        dropdown.style.display = "block";
        setHighlightedIndex(0);
      }
      return;
    }

    // Show dropdown when Backspace or Delete is pressed
    if (e.key === "Backspace" || e.key === "Delete") {
      if (dropdown) {
        dropdown.style.display = "block";
        setHighlightedIndex(0);
      }
      return;
    }

    if (filtered.length === 0) return;

    switch (e.key) {
      case "ArrowDown":
        e.preventDefault();
        if (dropdown && dropdown.style.display !== "block") {
          dropdown.style.display = "block";
        }
        setHighlightedIndex((prev) =>
          prev < filtered.length - 1 ? prev + 1 : prev
        );
        break;
      case "ArrowUp":
        e.preventDefault();
        if (dropdown && dropdown.style.display !== "block") {
          dropdown.style.display = "block";
        }
        setHighlightedIndex((prev) => (prev > 0 ? prev - 1 : 0));
        break;
      case "Escape":
        e.preventDefault();
        if (dropdown) {
          dropdown.style.display = "none";
        }
        break;
      default:
        break;
    }
  };

  // Input handling functions
  const handleInputChange = (rowIndex, colIndex, value, idOptions = {}) => {
    const columnId = getColumnType(colIndex);

    // Check if we need to fetch product groups by prefix
    if (
      columnId === "productGroup" &&
      value.length >= 3 &&
      value !== lastSearchedProductGroupPrefix
    ) {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
      searchTimeoutRef.current = setTimeout(() => {
        fetchProductGroupsByPrefix(value);
      }, 500);
    }

    setTableData((prevData) => {
      const newData = [...prevData];
      const row = { ...newData[rowIndex] };
      row.data = [...row.data];
      const cell = { ...row.data[colIndex], value };
      if (idOptions.setProductGroupId != null) {
        cell.productGroupId = idOptions.setProductGroupId;
      } else if (columnId === "productGroup") {
        delete cell.productGroupId;
      }
      if (idOptions.setSupplierId != null) {
        cell.supplierId = idOptions.setSupplierId;
      } else if (columnId === "supplier") {
        delete cell.supplierId;
      }
      row.data[colIndex] = cell;

      // const columnId = row.data[colIndex].id; // Already got it outside

      // Get the labor type value
      const lbrTypeCell = row.data.find((cell) => cell.id === "lType");
      const lbrType = lbrTypeCell?.value || "";

      // If GWt or LWt is changed, calculate NetWt
      if (columnId === "grWt" || columnId === "lsWtVepari") {
        const grossWeightCell = row.data.find((cell) => cell.id === "grWt");
        const lessWeightCell = row.data.find((cell) => cell.id === "lsWtVepari");
        const netWeightCell = row.data.find((cell) => cell.id === "netWt");

        if (grossWeightCell && lessWeightCell && netWeightCell) {
          const netWeight = calculateNetWeight(
            grossWeightCell.value,
            lessWeightCell.value
          );
          netWeightCell.value = netWeight;
        }
      }

      // Calculate Lbr Amt based on Lbr Type
      const grossWeightCell = row.data.find((cell) => cell.id === "grWt");
      const netWeightCell = row.data.find((cell) => cell.id === "netWt");
      const pcsCell = row.data.find((cell) => cell.id === "pcs");
      const lbrPercentageCell = row.data.find(
        (cell) => cell.id === "lbrPercentage"
      );
      const lbrRateCell = row.data.find((cell) => cell.id === "lbrRate");
      const lbrAmtCell = row.data.find((cell) => cell.id === "lbrAmt");

      // Direct rate entry for NWt/GWt/Pcs: leave percentage blank (do not back-calculate)
      if (columnId === "lbrRate" && lbrType !== "Fixed") {
        if (lbrPercentageCell) {
          lbrPercentageCell.value = "";
        }
      }

      // Calculate Lbr Rate if Lbr % is updated (only if lType is not Fixed)
      if (columnId === "lbrPercentage" && lbrType !== "Fixed") {
        const productGroupCell = row.data.find(
          (cell) => cell.id === "productGroup"
        );
        const selectedProductGroup = dropdownData.productGroup.find(
          (pg) => pg.label === productGroupCell?.value
        );
        const metalType = selectedProductGroup?.metal || "Gold"; // Default to Gold if not found
        const masterRate =
          metalType === "Gold" ? masterGoldRate : masterSilverRate;

        const lbrPercentage = parseFloat(value) || 0;
        const calculatedLbrRate =
          (lbrPercentage / 100) * calculatePerGramRate(masterRate);
        if (lbrRateCell) {
          lbrRateCell.value = calculatedLbrRate.toFixed(2);
        }
      }

      if (
        (columnId === "netWt" ||
          columnId === "grWt" ||
          columnId === "lsWtVepari" ||
          columnId === "pcs" ||
          columnId === "lType" ||
          columnId === "lbrPercentage" ||
          columnId === "lbrRate") &&
        lbrType !== "Fixed"
      ) {
        // Get values (after sync so rate entry uses blank %)
        const gwValue = parseFloat(grossWeightCell?.value) || 0;
        const nwValue = parseFloat(netWeightCell?.value) || 0;
        const pcsValue = parseFloat(pcsCell?.value) || 0;
        const lbrPercentage = parseFloat(lbrPercentageCell?.value) || 0;
        const lbrRate = parseFloat(lbrRateCell?.value) || 0;

        // Determine base weight based on Lbr Type
        let baseWeight = 0;
        if (lbrType === "NWt") {
          baseWeight = nwValue;
        } else if (lbrType === "GWt") {
          baseWeight = gwValue;
        } else if (lbrType === "Pcs") {
          baseWeight = pcsValue;
        }

        // Calculate Lbr Amt
        let lbrAmt = 0;
        const productGroupCell = row.data.find(
          (cell) => cell.id === "productGroup"
        );
        const selectedProductGroup = dropdownData.productGroup.find(
          (pg) => pg.label === productGroupCell?.value
        );
        const metalType = selectedProductGroup?.metal || "Gold"; // Default to Gold if not found

        if (lbrPercentage > 0) {
          const masterRate =
            metalType === "Gold" ? masterGoldRate : masterSilverRate;
          lbrAmt = calculateLbrAmtWithPercentage(
            baseWeight,
            lbrPercentage,
            masterRate
          );
        } else if (lbrRate > 0) {
          lbrAmt = calculateLbrAmtWithRate(baseWeight, lbrRate);
        }

        // Update Lbr Amt cell automatically for non-Fixed types
        if (lbrAmtCell) {
          // Round to the nearest whole number for display
          lbrAmtCell.value = Math.round(lbrAmt).toString();
        }
      }

      newData[rowIndex] = row;
      return newData;
    });

    if (columnId === "supplier") {
      if (value && value.length >= 2) {
        if (
          !lastSearchedSupplierPrefix ||
          !value.startsWith(lastSearchedSupplierPrefix)
        ) {
          fetchSuppliers(value);
        }
      } else if (!value || value.length === 0) {
        setDropdownData((prev) => ({
          ...prev,
          supplier: [],
        }));
        setLastSearchedSupplierPrefix("");
      }
    }
  };

  // Dropdown handling
  const handleDropdownClick = (rowIndex, colIndex, columnType) => {
    const inputElement = inputRefs.current[`${rowIndex}-${colIndex}`];
    if (!inputElement) return;

    // Close any existing dropdown first
    setActiveDropdown({ row: null, col: null, type: null });

    // Then open the new dropdown
    setTimeout(() => {
      setActiveDropdown({
        row: rowIndex,
        col: colIndex,
        type: columnType,
      });

      const rect = inputElement.getBoundingClientRect();
      setDropdownPosition({
        top: rect.bottom,
        left: rect.left,
        width: rect.width,
      });

      setHighlightedIndex(0);
    }, 0);
  };

  const handleDropdownSelect = (rowIndex, colIndex, selectedItem) => {
    const columnId = COLUMNS[colIndex]?.id;
    const value =
      typeof selectedItem === "object"
        ? selectedItem.label || selectedItem.name || ""
        : selectedItem;
    const idOpts =
      typeof selectedItem === "object" && selectedItem
        ? columnId === "productGroup"
          ? { setProductGroupId: selectedItem.value }
          : columnId === "supplier"
            ? { setSupplierId: selectedItem.value }
            : {}
        : {};
    handleInputChange(rowIndex, colIndex, value, idOpts);
    setActiveDropdown({ row: null, col: null, type: null });
  };

  // Modal handling
  const openImageModal = (imageUrl, rowIndex = null, colIndex = null) => {
    setPreviewImageInfo({ url: imageUrl, rowIndex, colIndex });
    setShowImageModal(true);
  };

  const closeImageModal = () => {
    setShowImageModal(false);
    setPreviewImageInfo({ url: null, rowIndex: null, colIndex: null });
  };

  // Modified handleSaveChanges - works with your existing API
  const handleSaveChanges = async () => {
    try {
      // 1. Build products array
      const productEntries = [];

      for (let rowIndex = 0; rowIndex < tableData.length; rowIndex++) {
        const row = tableData[rowIndex];
        const rowData = row.data.reduce((acc, cell) => {
          acc[cell.id] = cell.value;
          return acc;
        }, {});

        const productGroupCell = row.data.find((c) => c.id === "productGroup");
        const supplierCell = row.data.find((c) => c.id === "supplier");

        let resolvedProductGroupId =
          productGroupCell?.productGroupId != null
            ? Number(productGroupCell.productGroupId)
            : null;
        if (!Number.isFinite(resolvedProductGroupId)) {
          resolvedProductGroupId = resolveProductGroupId(
            rowData.productGroup,
            dropdownData.productGroup,
          );
        }
        if (!Number.isFinite(resolvedProductGroupId) && row.productGroupId) {
          resolvedProductGroupId = Number(row.productGroupId);
        }

        let resolvedSupplierId =
          supplierCell?.supplierId != null
            ? Number(supplierCell.supplierId)
            : null;
        if (!Number.isFinite(resolvedSupplierId)) {
          resolvedSupplierId = resolveSupplierId(
            rowData.supplier,
            dropdownData.supplier,
          );
        }
        if (
          !Number.isFinite(resolvedSupplierId) &&
          row.supplierId &&
          String(rowData.supplier || "").trim()
        ) {
          resolvedSupplierId = Number(row.supplierId);
        }

        // Charges
        const otherAmtCell = row.data.find((cell) => cell.id === "othAmt");
        const otherAmountEntries = otherAmtCell?.otherAmountEntries || [];
        const charges = mapOtherAmountEntriesForUpdate(otherAmountEntries);

        // LBR amount
        let lbrAmount = 0;
        const lbrAmtCell = row.data.find((cell) => cell.id === "lbrAmt");
        if (lbrAmtCell && lbrAmtCell.value) {
          lbrAmount = Number(lbrAmtCell.value) || 0;
        }

        // Image name
        const imageCell = row.data.find((cell) => cell.id === "image");
        let imageName = "";
        if (imageCell && imageCell.imageFile) {
          imageName = imageCell.imageFile.name;
        } else if (imageCell && imageCell.value) {
          imageName = imageCell.value;
        }

        productEntries.push({
          id: row.id,
          product_group: resolvedProductGroupId || "",
          huid: rowData.huidNo
            ? rowData.huidNo
                .split(",")
                .map((huid) => huid.trim())
                .join("##")
            : "",
          design: rowData.design || "",
          size: rowData.size || "",
          pcs: Number(rowData.pcs) || 0,
          GWT: Number(rowData.grWt) || 0,
          LWt_vepari: Number(rowData.lsWtVepari) || 0,
          LWt_customer: Number(rowData.lsWtCustomer) || 0,
          NWT: Number(rowData.netWt) || 0,
          LBR: Number(rowData.lbrPercentage) || 0,
          LBR_type: rowData.lType || "",
          LBR_rate: Number(rowData.lbrRate) || 0,
          LBR_amt: lbrAmount,
          othamt: Number(rowData.othAmt) || 0,
          MRP: Number(rowData.mrp) || 0,
          Supplier:
            resolvedSupplierId != null && Number.isFinite(resolvedSupplierId)
              ? resolvedSupplierId
              : null,
          status: rowData.status || "",
          image: imageName,
          image_file: imageCell?.imageFile || null,
          image_name: imageName,
          charges: charges,
        });
      }

      const branch_id = getSessionBranchId();

      const results = await Promise.all(
        productEntries.map((entry) => {
          const charges = Array.isArray(entry.charges) ? entry.charges : [];

          const status =
            String(entry.status) === "1" || String(entry.status).toLowerCase() === "active"
              ? "ACTIVE"
              : String(entry.status) === "0" ||
                String(entry.status).toLowerCase() === "inactive"
                ? "INACTIVE"
                : entry.status || undefined;

          const pcs = Number(entry.pcs) || 0;

          const lbrPercent = Number(entry.LBR);
          const lbrRate = Number(entry.LBR_rate);
          const selectedLbrTypeRaw = String(entry.LBR_type || "").trim();
          const selectedLbrTypeUpper = selectedLbrTypeRaw.toUpperCase();
          let apiLbrType = null;
          if (
            selectedLbrTypeUpper === "PERCENT" ||
            selectedLbrTypeUpper === "PER_GRAM" ||
            selectedLbrTypeUpper === "FLAT"
          ) {
            apiLbrType = selectedLbrTypeUpper;
          } else if (selectedLbrTypeUpper === "FIXED") {
            apiLbrType = "FLAT";
          } else if (selectedLbrTypeRaw) {
            // Preserve existing UI labels (e.g. NWt/GWt/Pcs) if present.
            apiLbrType = selectedLbrTypeRaw;
          } else if (lbrPercent > 0) {
            apiLbrType = "PERCENT";
          } else if (lbrRate > 0) {
            apiLbrType = "PER_GRAM";
          } else {
            apiLbrType = "FLAT";
          }

          const payload = {
            id: Number(entry.id),
            branch_id,
            product_group_id: Number(entry.product_group) || null,
            pcs,
            current_pcs: pcs,
            huid: entry.huid || "",
            design: entry.design || "",
            size: entry.size || "",
            image: entry.image || null,
            image_file: entry.image_file || undefined,
            supplier_id:
              entry.Supplier != null && Number.isFinite(Number(entry.Supplier))
                ? Number(entry.Supplier)
                : null,
            // Weight details
            gwt: Number(entry.GWT) || 0,
            lwt_vepari: Number(entry.LWt_vepari || entry.LWT_vepari) || 0,
            lwt_customer: Number(entry.LWt_customer || entry.LWT_customer) || 0,
            nwt: Number(entry.NWT) || 0,
            // Labor & pricing
            lbr_type: apiLbrType,
            lbr_percent: Number.isFinite(lbrPercent) && lbrPercent > 0 ? lbrPercent : null,
            lbr_rate: Number.isFinite(lbrRate) && lbrRate > 0 ? lbrRate : null,
            lbr_amt: Number(entry.LBR_amt) || 0,
            oth_amt: Number(entry.othamt) || 0,
            mrp: Number(entry.MRP) || 0,
            status,
            charges,
          };

          console.log("[EditStock] updateStock payload", payload);

          return updateStock(payload);
        }),
      );

      const failed = results.find((r) => !r?.success);
      if (failed) {
        toast.error(failed?.message || "Failed to update products");
        return;
      }

        toast.success("Products updated successfully");
        setShowSuccessModal(true);
        setTimeout(() => {
          setShowSuccessModal(false);
          navigate("/stocktable");
        }, 2000);
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to update products");
    }
  };

  const handleImageUpload = async (file, rowIndex) => {
    // Create a preview of the image
    const fileReader = new FileReader();
    fileReader.onload = async (e) => {
      // Update the image preview and store the file in the table data
      setTableData((prevData) => {
        const updatedData = [...prevData];
        if (updatedData[rowIndex] && updatedData[rowIndex].data) {
          const imageCell = updatedData[rowIndex].data.find(
            (cell) => cell.id === "image"
          );
          if (imageCell) {
            imageCell.imagePreview = e.target.result;
            imageCell.imageFile = file; // Store the actual file object
            imageCell.value = null; // Clear any previous uploaded image name
            imageCell.imageCandidates = [e.target.result];
          }
        }
        return updatedData;
      });
    };
    fileReader.readAsDataURL(file);
  };

  // UI Helper functions
  const isDropdownColumn = (columnId) => {
    return ["productGroup", "supplier", "lType"].includes(columnId);
  };

  const isImageColumn = (columnId) => {
    return columnId === "image";
  };

  const getColumnType = (colIndex) => {
    return COLUMNS[colIndex]?.id || "";
  };

  const handleFocus = (rowIndex, colIndex) => {
    const input = inputRefs.current[`${rowIndex}-${colIndex}`];
    if (input) {
      input.style.backgroundColor = "#f8f9fa";
      input.select();
    }
  };

  const triggerFileInput = (rowIndex, colIndex) => {
    if (fileInputRefs.current[`${rowIndex}-${colIndex}`]) {
      fileInputRefs.current[`${rowIndex}-${colIndex}`].click();
    }
  };

  // Render functions
  const renderDropdown = () => {
    if (
      activeDropdown.row === null ||
      activeDropdown.col === null ||
      !activeDropdown.type
    ) {
      return null;
    }

    const cell = tableData[activeDropdown.row].data[activeDropdown.col];
    const filtered = getFilteredItems(cell.value, activeDropdown.type);

    // Reset refs array
    dropdownItemsRef.current = [];

    // Special styling for productGroup dropdown - match Sales.jsx pattern
    const isProductGroup = activeDropdown.type === "productGroup";
    // For productGroup: use 5 items with 40px height each (matching Sales.jsx)
    // For others: use 5 items with 35px height each
    const VISIBLE_COUNT = isProductGroup ? 5 : 5;
    const ITEM_HEIGHT = isProductGroup ? 40 : 35;
    const visibleItems = Math.min(filtered.length, VISIBLE_COUNT);
    const dropdownHeight = visibleItems * ITEM_HEIGHT;

    // Calculate max width based on content (for non-productGroup dropdowns)
    const maxContentWidth = Math.max(
      ...filtered.map((item) => {
        const tempSpan = document.createElement("span");
        tempSpan.style.fontSize = "0.85rem";
        tempSpan.style.visibility = "hidden";
        tempSpan.style.position = "absolute";
        tempSpan.style.whiteSpace = "nowrap";

        // Safely get the label
        const itemLabel = typeof item === "object" ? item.label || "" : item;
        tempSpan.textContent = itemLabel;

        document.body.appendChild(tempSpan);
        const width = tempSpan.offsetWidth;
        document.body.removeChild(tempSpan);
        return width;
      }),
      dropdownPosition.width // minimum width is input width
    );

    // Calculate width for dropdowns - match Sales.jsx pattern
    let adjustedWidth;
    if (isProductGroup) {
      // ProductGroup: match Sales.jsx - Math.max(inputWidth, 200)
      adjustedWidth = Math.max(200, dropdownPosition.width);
    } else {
      // Other dropdowns: use content-based width
      adjustedWidth = Math.max(maxContentWidth + 10, 100);
    }

    const portalContainer = document.getElementById("dropdown-portal");
    if (!portalContainer) return null;

    return ReactDOM.createPortal(
      <div
        ref={dropdownRef}
        className="dropdown-menu show shadow-sm"
        style={{
          position: "absolute",
          top: `${dropdownPosition.top}px`,
          left: `${dropdownPosition.left}px`,
          width: `${adjustedWidth}px`,
          maxHeight: isProductGroup
            ? `${dropdownHeight + 16}px`
            : `${dropdownHeight}px`,
          height: isProductGroup
            ? `${dropdownHeight + 16}px`
            : `${dropdownHeight}px`,
          overflow: "hidden",
          zIndex: 9999,
          border: "1px solid rgba(0,0,0,.15)",
          borderRadius: "0.25rem",
          backgroundColor: "#fff",
          boxSizing: "border-box",
          boxShadow: "0 0.5rem 1rem rgba(0, 0, 0, 0.15)",
        }}
      >
        <div
          style={{
            maxHeight: `${dropdownHeight}px`,
            height: `${dropdownHeight}px`,
            overflowY: filtered.length > VISIBLE_COUNT ? "auto" : "hidden",
            overflowX: "hidden",
          }}
        >
        {filtered.length > 0 ? (
          filtered.map((item, idx) => {
            // Handle both object and string types
            const itemLabel =
                typeof item === "object"
                  ? isProductGroup
                    ? productGroupOptionDisplayName(item)
                    : item.label || ""
                  : item;
            const isSelected =
              typeof item === "object"
                ? (item.label || "").toLowerCase() ===
                  (cell.value || "").toLowerCase()
                : item.toLowerCase() === (cell.value || "").toLowerCase();

            return (
              <div
                key={idx}
                ref={(el) => (dropdownItemsRef.current[idx] = el)}
                  className={`dropdown-item ${idx === highlightedIndex ? "active" : ""
                }`}
                title={itemLabel}
                style={{
                  cursor: "pointer",
                  padding: isProductGroup ? "0.4rem 0.75rem" : "0.25rem 1rem",
                  fontSize: "0.85rem",
                  backgroundColor:
                    idx === highlightedIndex
                      ? "var(--theme-primary)"
                      : isSelected
                      ? "#f0f0f0"
                      : "transparent",
                  color: idx === highlightedIndex ? "#fff" : "inherit",
                  whiteSpace: isProductGroup ? "normal" : "nowrap",
                  wordWrap: isProductGroup ? "break-word" : "normal",
                  overflow: isProductGroup ? "visible" : "hidden",
                  textOverflow: isProductGroup ? "clip" : "ellipsis",
                }}
                onMouseEnter={() => setHighlightedIndex(idx)}
                onMouseDown={() =>
                  handleDropdownSelect(
                    activeDropdown.row,
                    activeDropdown.col,
                    item
                  )
                }
              >
                {itemLabel}
              </div>
            );
          })
        ) : (
          <div className="dropdown-item disabled">No matching items</div>
        )}
        </div>
      </div>,
      portalContainer
    );
  };

  // Define a generic submit handler for form submissions
  const handleSubmit = (e) => {
    e.preventDefault();
    // Placeholder function to handle form submissions
    // console.log("Form submitted");
  };

  // Render methods
  const renderImageModal = () => {
    if (!showImageModal) return null;

    const portalElement = document.getElementById("modal-portal");
    if (!portalElement) return null;

    return ReactDOM.createPortal(
      <Modal show={showImageModal} onHide={closeImageModal} centered size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Image Preview</Modal.Title>
        </Modal.Header>
        <Modal.Body className="text-center">
          {previewImageInfo.url && (
            <img
              src={previewImageInfo.url}
              alt="Product Preview"
              className="img-fluid"
              style={{ maxHeight: "70vh" }}
              onError={(e) => {
                const { rowIndex, colIndex } = previewImageInfo;
                if (rowIndex === null || colIndex === null) return;
                const cell = tableData[rowIndex]?.data?.[colIndex];
                const nextUrl = getNextCandidate(
                  cell?.imageCandidates,
                  e.currentTarget.src,
                );
                if (!nextUrl) return;
                handleCellImageError(rowIndex, colIndex, e.currentTarget.src);
                setPreviewImageInfo((prev) => ({ ...prev, url: nextUrl }));
              }}
            />
          )}
          {previewImageInfo.rowIndex !== null &&
            previewImageInfo.colIndex !== null && (
              <div className="d-flex justify-content-center gap-3 mt-3">
                <Button
                  variant="primary"
                  onClick={() => {
                    // Trigger the hidden file input for the specific row and column
                    const fileInput =
                      fileInputRefs.current[
                        `${previewImageInfo.rowIndex}-${previewImageInfo.colIndex}`
                      ];
                    if (fileInput) {
                      fileInput.click();
                    }
                    closeImageModal();
                  }}
                >
                  <Icon
                    icon="solar:gallery-edit-bold"
                    width="18"
                    height="18"
                    className="me-2"
                  />
                  Change Image
                </Button>
                <Button
                  variant="danger"
                  onClick={() => {
                    setTableData((prevData) => {
                      const updatedData = [...prevData];
                      if (
                        updatedData[previewImageInfo.rowIndex] &&
                        updatedData[previewImageInfo.rowIndex].data
                      ) {
                        const imageCell = updatedData[
                          previewImageInfo.rowIndex
                        ].data.find((c) => c.id === "image");
                        if (imageCell) {
                          imageCell.value = "";
                          imageCell.imageFile = null;
                          imageCell.imagePreview = null;
                        }
                      }
                      return updatedData;
                    });
                    closeImageModal();
                  }}
                >
                  <Icon
                    icon="solar:trash-bin-trash-bold"
                    width="18"
                    height="18"
                    className="me-2"
                  />
                  Delete Image
                </Button>
              </div>
            )}
        </Modal.Body>
      </Modal>,
      portalElement
    );
  };

  const renderSuccessModal = () => {
    if (!showSuccessModal) return null;

    const portalElement = document.getElementById("modal-portal");
    if (!portalElement) return null;

    return ReactDOM.createPortal(
      <Modal
        show={showSuccessModal}
        onHide={() => {
          setShowSuccessModal(false);
        }}
        centered
        backdrop="static"
        keyboard={false}
        animation={false}
        className="success-modal"
      ></Modal>,
      portalElement
    );
  };

  // An additional cleanup effect that runs only on component unmount
  useEffect(() => {
    return () => {
      // Cleanup function for component unmount
      try {
        // Remove any tooltips that might be active
        if (tooltipTimeoutRef.current) {
          clearTimeout(tooltipTimeoutRef.current);
          tooltipTimeoutRef.current = null;
        }
      } catch (err) {
        // console.error("Error in cleanup:", err);
      }
    };
  }, []); // Empty dependency array ensures this runs only on unmount

  // State for charge type suggestions
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [selectedSuggestionIndex, setSelectedSuggestionIndex] = useState(0);
  const suggestionTimeoutRef = useRef(null);

  // Charge types data with multiple codes
  const chargeTypes = useOtherChargeOptions();


  // Function to get charge type suggestions
  const getSuggestions = (query) => {
    if (!query || query.length < 1) {
      setSuggestions([]);
      return;
    }

    const searchQuery = query.toLowerCase();

    // First try to match by any code
    let matchedByCode = chargeTypes.filter((charge) =>
      charge.codes.some((code) => code.toLowerCase() === searchQuery)
    );

    // If no exact code match, then try partial matches on codes
    if (matchedByCode.length === 0) {
      matchedByCode = chargeTypes.filter((charge) =>
        charge.codes.some((code) => code.toLowerCase().startsWith(searchQuery))
      );
    }

    // Get matches by name
    const matchedByName = chargeTypes.filter(
      (charge) =>
        charge.name.toLowerCase().includes(searchQuery) &&
        !matchedByCode.some((codeMatch) => codeMatch.name === charge.name)
    );

    // Combine results with code matches first
    const matchedSuggestions = [...matchedByCode, ...matchedByName].map(
      (charge) => `${charge.name} (${charge.codes[0]})`
    );

    setSuggestions(matchedSuggestions);
    setShowSuggestions(true);
    setSelectedSuggestionIndex(0);
  };


  const [lastHuidEnterPress, setLastHuidEnterPress] = useState({
    time: 0,
    rowIndex: null,
  });
  const [huidModal, setHuidModal] = useState({ isOpen: false, rowIndex: null });

  const openHuidModal = (rowIndex) => {
    setHuidModal({ isOpen: true, rowIndex });
  };

  const closeHuidModal = () => {
    setHuidModal({ isOpen: false, rowIndex: null });
  };

  const handleHuidKeyDown = (e, rowIndex) => {
    if (e.key === "Enter") {
      const now = Date.now();
      if (
        lastHuidEnterPress.rowIndex === rowIndex &&
        now - lastHuidEnterPress.time < 500
      ) {
        // 500ms threshold for double-Enter
        e.preventDefault();
        openHuidModal(rowIndex);
        setLastHuidEnterPress({ time: 0, rowIndex: null });
      } else {
        setLastHuidEnterPress({ time: now, rowIndex });
      }
    }
  };

  const renderHuidModal = () => {
    if (!huidModal.isOpen) return null;

    return (
      <Modal show={huidModal.isOpen} onHide={closeHuidModal} centered>
        <Modal.Header closeButton>
          <Modal.Title>HUID Details</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="mb-3">
            <label className="form-label">HUID Number</label>
            <input
              type="text"
              className="form-control"
              value={
                tableData[huidModal.rowIndex]?.data
                  .find((cell) => cell.id === "huidNo")
                  ?.value.replace(/##/g, ", ") || ""
              }
              onChange={(e) => {
                handleInputChange(
                  huidModal.rowIndex,
                  tableData[huidModal.rowIndex]?.data.findIndex(
                    (cell) => cell.id === "huidNo"
                  ),
                  e.target.value.replace(/, /g, "##")
                );
              }}
            />
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={closeHuidModal}>
            Close
          </Button>
          <Button
            variant="primary"
            className={buttonClass}
            onClick={closeHuidModal}
          >
            Save changes
          </Button>
        </Modal.Footer>
      </Modal>
    );
  };

  return (
    <div className="body-wrapper">
      <div className="container-fluid">
        <div className="card w-100 position-relative overflow-hidden">
          <div className="px-4 py-3 border-bottom d-flex justify-content-between align-items-center">
            <h4 className="card-title mb-0">Edit Products</h4>
          </div>

          <div className="card-body p-4">
            {isLoading ? (
              <div className="text-center py-5">
                <div className="spinner-border text-primary" role="status">
                  <span className="visually-hidden">Loading...</span>
                </div>
                <p className="mt-3">Loading product data...</p>
              </div>
            ) : selectedItemIds.length === 0 ? (
              <div className="alert alert-warning text-center">
                <h5>No Products Selected</h5>
                <p>
                  Please go back to the stock table and select products to edit.
                </p>
                <button
                  className={`btn btn-primary mt-3 ${buttonClass}`}
                  onClick={() => navigate("/stocktable")}
                >
                  <Icon
                    icon="solar:arrow-left-bold"
                    width="18"
                    height="18"
                    className="me-2"
                  />
                  Go to Stock Table
                </button>
              </div>
            ) : tableData.length === 0 ? (
              <div className="alert alert-warning">
                No products found for editing. Please return to the stock table
                and select products.
              </div>
            ) : (
              <div className="row mb-1">
                <div className="col-sm-6 col-md-2">
                  <input
                    type="text"
                    id="date"
                    className="form-control"
                    value={new Date().toLocaleDateString("en-GB")}
                    readOnly
                  />
                </div>
              </div>
            )}

            <div
              ref={tableContainerRef}
              className="table-responsive border rounded-2 mt-3"
              style={{
                maxHeight:
                  tableData.length > 6
                    ? `${40 + 6 * 38}px`
                    : undefined,
                overflowY: tableData.length > 6 ? "auto" : "visible",
              }}
            >
              <table className="table table-bordered table-hover table-sm mb-0 theme-header-table">
                <thead>
                  <tr>
                    {COLUMNS.map((column) => (
                      <th
                        key={column.id}
                        className="px-2 py-2 text-center"
                        style={{
                          fontSize: "0.85rem",
                          width: column.width,
                          fontWeight: "600",
                          whiteSpace: "nowrap",
                          position: tableData.length > 6 ? "sticky" : undefined,
                          top: tableData.length > 6 ? 0 : undefined,
                          zIndex: tableData.length > 6 ? 5 : undefined,
                        }}
                      >
                        {column.label}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {tableData.map((row, rowIndex) => (
                    <tr key={row.id} style={{ backgroundColor: "#ffffff" }}>
                      {row.data.map((cell, colIndex) => {
                        // Add special handling for serial number column
                        if (cell.id === "no") {
                          return (
                            <td
                              key={cell.id}
                              className="p-0"
                              style={{ width: COLUMNS[colIndex]?.width }}
                            >
                              <div
                                className="d-flex align-items-center justify-content-center"
                                style={{ height: "38px" }}
                              >
                                <span style={{ fontSize: "0.85rem" }}>
                                  {rowIndex + 1}
                                </span>
                              </div>
                            </td>
                          );
                        }

                        // Tag No should not be editable
                        if (cell.id === "tagNo") {
                          return (
                            <td
                              key={cell.id}
                              className="p-0"
                              style={{ width: COLUMNS[colIndex]?.width }}
                            >
                              <div
                                className="d-flex align-items-center px-2"
                                style={{
                                  height: "38px",
                                  fontSize: "0.85rem",
                                  color: "#6c757d",
                                  backgroundColor: "#f8f9fa",
                                }}
                                title={cell.value || ""}
                              >
                                {cell.value || "-"}
                              </div>
                            </td>
                          );
                        }

                        // Check if this is the LWt from Vepari column and if there are Other Amount entries
                        const hasOtherAmountEntries =
                          row.data.find((c) => c.id === "othAmt")
                            ?.otherAmountEntries?.length > 0;

                        return (
                          <td
                            key={cell.id}
                            className="p-0"
                            style={{
                              width: COLUMNS[colIndex]?.width,
                              position: "relative",
                            }}
                          >
                            {isDropdownColumn(getColumnType(colIndex)) ? (
                              // Dropdown-enabled cell
                              <div className="position-relative">
                                <input
                                  ref={(el) =>
                                    (inputRefs.current[
                                      `${rowIndex}-${colIndex}`
                                    ] = el)
                                  }
                                  type="text"
                                  value={cell.value}
                                  onChange={(e) => {
                                    const newValue = e.target.value;
                                    handleInputChange(
                                      rowIndex,
                                      colIndex,
                                      newValue
                                    );
                                    const columnType = getColumnType(colIndex);
                                    const requiresMinLength =
                                      columnType === "productGroup";
                                    const meetsMinLength =
                                      !requiresMinLength ||
                                      newValue.trim().length >= 3;

                                    if (meetsMinLength) {
                                      setTimeout(() => {
                                        handleDropdownClick(
                                          rowIndex,
                                          colIndex,
                                          columnType
                                        );
                                      }, 0);
                                    } else if (columnType === "productGroup") {
                                      setActiveDropdown({
                                        row: null,
                                        col: null,
                                        type: null,
                                      });
                                    }
                                  }}
                                  onKeyDown={(e) =>
                                    handleKeyDown(
                                      e,
                                      rowIndex,
                                      colIndex,
                                      getColumnType(colIndex)
                                    )
                                  }
                                  onClick={() => {
                                    const columnType = getColumnType(colIndex);
                                    if (columnType === "productGroup") {
                                      // For productGroup, only show dropdown if user has typed 3+ characters AND we have results or API was called
                                      const productGroupCell = tableData[rowIndex]?.data?.find(
                                        (cell) => cell.id === "productGroup"
                                      );
                                      const productGroupValue = (productGroupCell?.value || "")
                                        .toString()
                                        .trim();
                                      const hasEnoughChars = productGroupValue.length >= 3;
                                      const hasResults =
                                        getFilteredItems(productGroupValue, "productGroup").length > 0;

                                      // Only show dropdown if user has typed 3+ characters AND (has results OR API was called)
                                      if (hasEnoughChars && (hasResults || productGroupApiCalled)) {
                                        handleDropdownClick(rowIndex, colIndex, columnType);
                                      }
                                    } else {
                                      handleDropdownClick(rowIndex, colIndex, columnType);
                                    }
                                  }}
                                  className="form-control border-0 rounded-0"
                                  style={{
                                    fontSize: "0.85rem",
                                    padding: "0.4rem 0.5rem",
                                    backgroundColor: "transparent",
                                    width: "100%",
                                    transition: "background-color 0.2s",
                                  }}
                                  onFocus={() =>
                                    handleFocus(rowIndex, colIndex)
                                  }
                                  onBlur={(e) => {
                                    e.target.style.backgroundColor =
                                      "transparent";
                                  }}
                                  autoComplete="off"
                                />
                              </div>
                            ) : isImageColumn(getColumnType(colIndex)) ? (
                              <div
                                className="position-relative d-flex align-items-center justify-content-center"
                                style={{ height: "38px" }}
                              >
                                <input
                                  type="file"
                                  accept="image/*"
                                  ref={(el) =>
                                    (fileInputRefs.current[
                                      `${rowIndex}-${colIndex}`
                                    ] = el)
                                  }
                                  style={{ display: "none" }}
                                  onChange={(e) => {
                                    const file = e.target.files[0];
                                    if (!file) return;

                                    // Check if file is an image
                                    if (!file.type.match("image.*")) {
                                      alert("Please select an image file");
                                      return;
                                    }

                                    // Check file size (limit to 5MB)
                                    if (file.size > 5 * 1024 * 1024) {
                                      alert("File size should not exceed 5MB");
                                      return;
                                    }

                                    handleImageUpload(file, rowIndex);
                                  }}
                                />

                                {cell.imagePreview ? (
                                  <div
                                    className="position-relative"
                                    style={{ height: "38px" }}
                                  >
                                    <div
                                      className="d-flex justify-content-center align-items-center h-100"
                                      onMouseEnter={(e) => {
                                        // Clear any existing timeout
                                        if (tooltipTimeoutRef.current) {
                                          clearTimeout(
                                            tooltipTimeoutRef.current
                                          );
                                          tooltipTimeoutRef.current = null;
                                        }

                                        // Hide all tooltips first
                                        Object.values(
                                          tooltipRefs.current
                                        ).forEach((tooltip) => {
                                          if (tooltip)
                                            tooltip.style.display = "none";
                                        });

                                        const tooltipId = `tooltip-${rowIndex}-${colIndex}`;
                                        const tooltip =
                                          tooltipRefs.current[tooltipId];

                                        if (tooltip) {
                                          // Get the position of the image
                                          const rect =
                                            e.currentTarget.getBoundingClientRect();

                                          // Position the tooltip
                                          tooltip.style.display = "block";
                                          tooltip.style.top = `${rect.top - tooltip.offsetHeight - 5
                                          }px`;
                                          tooltip.style.left = `${rect.left +
                                            rect.width / 2 -
                                            tooltip.offsetWidth / 2
                                          }px`;
                                        }
                                      }}
                                      onMouseLeave={(e) => {
                                        const tooltipId = `tooltip-${rowIndex}-${colIndex}`;
                                        const tooltip =
                                          tooltipRefs.current[tooltipId];
                                        if (tooltip) {
                                          tooltip.style.display = "none";
                                        }
                                      }}
                                    >
                                      <button
                                        ref={(el) =>
                                          (imageBtnRefs.current[rowIndex] = el)
                                        }
                                        type="button"
                                        className="btn p-0 border-0 bg-transparent"
                                        style={{
                                          cursor: "pointer",
                                          display: "inline-flex",
                                          alignItems: "center",
                                          justifyContent: "center",
                                          outline: "none",
                                        }}
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          openImageModal(
                                            cell.imagePreview,
                                            rowIndex,
                                            colIndex
                                          );
                                        }}
                                        onKeyDown={(e) =>
                                          handleImageKeyDown(
                                            e,
                                            rowIndex,
                                            colIndex
                                          )
                                        }
                                        title="View Image"
                                      >
                                        <img
                                          src={cell.imagePreview}
                                          alt="Preview"
                                          className="img-thumbnail"
                                          style={{
                                            height: "30px",
                                            width: "30px",
                                            objectFit: "cover",
                                            cursor: "pointer",
                                          }}
                                          onError={(e) => {
                                            handleCellImageError(
                                              rowIndex,
                                              colIndex,
                                              e.currentTarget.src
                                            );
                                          }}
                                        />
                                      </button>
                                    </div>

                                    {/* Hover preview tooltip */}
                                    <div
                                      ref={(el) =>
                                        (tooltipRefs.current[
                                          `tooltip-${rowIndex}-${colIndex}`
                                        ] = el)
                                      }
                                      className="position-fixed shadow-lg rounded p-2 bg-white"
                                      style={{
                                        display: "none",
                                        zIndex: 9999,
                                      }}
                                    >
                                      <div className="text-center mb-3">
                                        <img
                                          src={cell.imagePreview}
                                          alt="Preview"
                                          className="img-fluid rounded"
                                          style={{
                                            maxHeight: "100px",
                                            objectFit: "contain",
                                          }}
                                          onError={(e) => {
                                            handleCellImageError(
                                              rowIndex,
                                              colIndex,
                                              e.currentTarget.src
                                            );
                                          }}
                                        />
                                      </div>
                                      <div className="d-flex justify-content-center gap-2">
                                        <Button
                                          variant="primary"
                                          size="sm"
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            triggerFileInput(
                                              rowIndex,
                                              colIndex
                                            );
                                          }}
                                          title="Change Image"
                                        >
                                          <Icon
                                            icon="solar:gallery-edit-bold"
                                            width="16"
                                            height="16"
                                          />
                                        </Button>
                                        <Button
                                          variant="danger"
                                          size="sm"
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            setTableData((prevData) => {
                                              const updatedData = [...prevData];
                                              const imageCell = updatedData[
                                                rowIndex
                                              ].data.find(
                                                (c) => c.id === "image"
                                              );
                                              if (imageCell) {
                                                imageCell.value = "";
                                                imageCell.imageFile = null;
                                                imageCell.imagePreview = null;
                                              }
                                              return updatedData;
                                            });
                                          }}
                                          title="Remove Image"
                                        >
                                          <Icon
                                            icon="solar:trash-bin-trash-bold"
                                            width="16"
                                            height="16"
                                          />
                                        </Button>
                                        <Button
                                          variant="primary"
                                          size="sm"
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            openImageModal(
                                              cell.imagePreview,
                                              rowIndex,
                                              colIndex
                                            );
                                          }}
                                          title="View Image"
                                        >
                                          <Icon
                                            icon="solar:eye-bold"
                                            width="16"
                                            height="16"
                                          />
                                        </Button>
                                      </div>
                                    </div>
                                  </div>
                                ) : (
                                  <Button
                                    ref={(el) =>
                                      (imageBtnRefs.current[rowIndex] = el)
                                    }
                                    variant="secondary"
                                    size="sm"
                                    onClick={() =>
                                      triggerFileInput(rowIndex, colIndex)
                                    }
                                    onKeyDown={(e) =>
                                      handleImageKeyDown(
                                        e,
                                        rowIndex,
                                        colIndex
                                      )
                                    }
                                    title="Upload Image"
                                  >
                                    <Icon
                                      icon="solar:gallery-add-bold"
                                      width="16"
                                      height="16"
                                    />
                                  </Button>
                                )}
                              </div>
                            ) : getColumnType(colIndex) === "othAmt" ? (
                              <div className="d-flex align-items-center position-relative">
                                <input
                                  ref={(el) =>
                                    (inputRefs.current[
                                      `${rowIndex}-${colIndex}`
                                    ] = el)
                                  }
                                  type="text"
                                  value={cell.value}
                                  onChange={(e) => {
                                    // Only allow changes if there are no entries
                                    if (!cell.otherAmountEntries?.length) {
                                      const value = e.target.value;
                                      if (
                                        value === "" ||
                                        /^\d*\.?\d*$/.test(value)
                                      ) {
                                        handleInputChange(
                                          rowIndex,
                                          colIndex,
                                          value
                                        );
                                      }
                                    }
                                  }}
                                  onKeyDown={(e) => {
                                    handleKeyDown(
                                      e,
                                      rowIndex,
                                      colIndex,
                                      getColumnType(colIndex)
                                    );
                                    handleOtherAmountInteraction(rowIndex, e);
                                  }}
                                  onDoubleClick={(e) =>
                                    handleOtherAmountInteraction(rowIndex, e)
                                  }
                                  onClick={(e) =>
                                    handleOtherAmountInteraction(rowIndex, e)
                                  }
                                  className="form-control border-0 rounded-0"
                                  style={{
                                    fontSize: "0.85rem",
                                    padding: "0.4rem 0.5rem",
                                    backgroundColor: "transparent",
                                    width: "100%",
                                    transition: "background-color 0.2s",
                                    cursor: "pointer",
                                  }}
                                  onFocus={() =>
                                    handleFocus(rowIndex, colIndex)
                                  }
                                  onBlur={(e) =>
                                    (e.target.style.backgroundColor =
                                      "transparent")
                                  }
                                  title={
                                    cell.otherAmountEntries?.length > 0
                                      ? "Double-click or press Enter twice to edit details"
                                      : "Enter amount directly or double-click/press Enter twice for details"
                                  }
                                />
                                {cell.otherAmountEntries?.length > 0 && (
                                  <div className="position-relative">
                                    <Button
                                      variant="link"
                                      className="p-0 px-1 text-primary"
                                      style={{
                                        fontSize: "1rem",
                                        position: "absolute",
                                        right: "5px",
                                        top: "50%",
                                        transform: "translateY(-50%)",
                                        zIndex: 1,
                                      }}
                                      onClick={(e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        const tooltipId = `oth-amt-tooltip-${rowIndex}`;
                                        const tooltip =
                                          tooltipRefs.current[tooltipId];

                                        if (tooltip) {
                                          // If tooltip is already visible, hide it
                                          if (
                                            tooltip.style.display === "block"
                                          ) {
                                            tooltip.style.display = "none";
                                            setActiveTooltip(null);

                                            // Clear any existing timeout
                                            if (tooltipTimeoutRef.current) {
                                              clearTimeout(
                                                tooltipTimeoutRef.current
                                              );
                                              tooltipTimeoutRef.current = null;
                                            }
                                            return;
                                          }

                                          // Hide any other visible tooltips first
                                          Object.entries(
                                            tooltipRefs.current
                                          ).forEach(([id, t]) => {
                                            if (t && id !== tooltipId) {
                                              t.style.display = "none";
                                            }
                                          });

                                          // Position and show tooltip
                                          positionTooltip(
                                            e.currentTarget,
                                            tooltip
                                          );

                                          // Set this as the active tooltip
                                          setActiveTooltip(tooltipId);

                                          // Set timeout to hide after 4 seconds
                                          if (tooltipTimeoutRef.current) {
                                            clearTimeout(
                                              tooltipTimeoutRef.current
                                            );
                                          }

                                          tooltipTimeoutRef.current =
                                            setTimeout(() => {
                                              if (tooltip) {
                                                tooltip.style.display = "none";
                                                setActiveTooltip(null);
                                                tooltipTimeoutRef.current =
                                                  null;
                                              }
                                            }, 4000);
                                        }
                                      }}
                                      onMouseEnter={(e) => {
                                        // Don't show on hover if already showing from a click
                                        if (tooltipTimeoutRef.current) return;

                                        const tooltipId = `oth-amt-tooltip-${rowIndex}`;
                                        const tooltip =
                                          tooltipRefs.current[tooltipId];

                                        if (tooltip) {
                                          // Hide any other visible tooltips first
                                          Object.entries(
                                            tooltipRefs.current
                                          ).forEach(([id, t]) => {
                                            if (t && id !== tooltipId) {
                                              t.style.display = "none";
                                            }
                                          });

                                          // Position and show tooltip
                                          positionTooltip(
                                            e.currentTarget,
                                            tooltip
                                          );

                                          // Set this as the active tooltip
                                          setActiveTooltip(tooltipId);
                                        }
                                      }}
                                      onMouseLeave={() => {
                                        // Only hide if not in clicked mode
                                        if (tooltipTimeoutRef.current) return;

                                        const tooltipId = `oth-amt-tooltip-${rowIndex}`;
                                        const currentTooltip =
                                          tooltipRefs.current[tooltipId];
                                        if (activeTooltip === tooltipId) {
                                          // Use setTimeout to allow moving to the tooltip
                                          setTimeout(() => {
                                            // Only hide if we're not hovering over the tooltip itself
                                            if (
                                              activeTooltip === tooltipId &&
                                              !currentTooltip?.matches(
                                                ":hover"
                                              ) &&
                                              !tooltipTimeoutRef.current
                                            ) {
                                              currentTooltip.style.display =
                                                "none";
                                              setActiveTooltip(null);
                                            }
                                          }, 100);
                                        }
                                      }}
                                    >
                                      <Icon icon="solar:info-circle-bold" />
                                    </Button>

                                    {/* Other Amount Details Tooltip */}
                                    <div
                                      ref={(el) =>
                                        (tooltipRefs.current[
                                          `oth-amt-tooltip-${rowIndex}`
                                        ] = el)
                                      }
                                      className="shadow-lg rounded p-2 bg-white tooltip-container"
                                      style={{
                                        display: "none",
                                        width: "auto",
                                        maxWidth: "250px",
                                        zIndex: 9999,
                                        position: "fixed",
                                      }}
                                      onMouseEnter={() => {
                                        // Do nothing when hovering tooltip
                                        // This prevents the tooltip from disappearing
                                      }}
                                      onMouseLeave={(e) => {
                                        // Only hide if not in clicked mode
                                        if (tooltipTimeoutRef.current) return;

                                        const tooltipId = `oth-amt-tooltip-${rowIndex}`;
                                        const currentTooltip =
                                          tooltipRefs.current[tooltipId];
                                        if (activeTooltip === tooltipId) {
                                          e.currentTarget.style.display =
                                            "none";
                                          setActiveTooltip(null);
                                        }
                                      }}
                                    >
                                      <div
                                        className="tooltip-arrow"
                                        style={{
                                          position: "absolute",
                                          width: "12px",
                                          height: "12px",
                                          backgroundColor: "white",
                                          border: "1px solid rgba(0,0,0,0.1)",
                                          transform: "rotate(45deg)",
                                          borderRight: "none",
                                          borderBottom: "none",
                                        }}
                                      />
                                      <div
                                        className="other-amount-details"
                                        style={{
                                          maxHeight: "200px",
                                          overflowY: "auto",
                                        }}
                                      >
                                        {cell.otherAmountEntries.map(
                                          (entry, idx) => (
                                            <div
                                              key={idx}
                                              className="d-flex justify-content-between py-1"
                                              style={{
                                                borderBottom:
                                                  idx !==
                                                  cell.otherAmountEntries
                                                    .length -
                                                    1
                                                    ? "1px solid #eee"
                                                    : "none",
                                              }}
                                            >
                                              <span
                                                style={{
                                                  fontSize: "0.85rem",
                                                  flex: "1",
                                                  marginRight: "20px",
                                                }}
                                              >
                                                {entry.chargeType}
                                              </span>
                                              <span
                                                style={{
                                                  fontSize: "0.85rem",
                                                  minWidth: "80px",
                                                  textAlign: "right",
                                                }}
                                              >
                                                ₹{" "}
                                                {Number(entry.amount).toFixed(
                                                  2
                                                )}
                                              </span>
                                            </div>
                                          )
                                        )}
                                        <div className="mt-2 pt-2 border-top">
                                          <div className="d-flex justify-content-between">
                                            <strong className="text-dark">
                                              Total
                                            </strong>
                                            <strong className="text-dark">
                                              ₹{" "}
                                              {cell.otherAmountEntries
                                                .reduce(
                                                  (sum, entry) =>
                                                    sum + Number(entry.amount),
                                                  0
                                                )
                                                .toFixed(2)}
                                            </strong>
                                          </div>
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                )}
                              </div>
                            ) : getColumnType(colIndex) === "lsWtVepari" ? (
                              <div className="d-flex align-items-center position-relative">
                                <input
                                  ref={(el) =>
                                    (inputRefs.current[
                                      `${rowIndex}-${colIndex}`
                                    ] = el)
                                  }
                                  type="text"
                                  value={cell.value}
                                  onChange={(e) => {
                                    // Only allow changes if there are no Other Amount entries
                                    if (!hasOtherAmountEntries) {
                                      const value = e.target.value;
                                      if (
                                        value === "" ||
                                        /^\d*\.?\d*$/.test(value)
                                      ) {
                                        handleInputChange(
                                          rowIndex,
                                          colIndex,
                                          value
                                        );
                                      }
                                    }
                                  }}
                                  className="form-control border-0 rounded-0"
                                  style={{
                                    fontSize: "0.85rem",
                                    padding: "0.4rem 0.5rem",
                                    backgroundColor: "transparent",
                                    width: "100%",
                                    transition: "background-color 0.2s",
                                    cursor: hasOtherAmountEntries
                                      ? "not-allowed"
                                      : "text",
                                  }}
                                  onFocus={(e) => {
                                    if (!hasOtherAmountEntries) {
                                      handleFocus(rowIndex, colIndex);
                                    }
                                  }}
                                  onBlur={(e) => {
                                    e.target.style.backgroundColor =
                                      "transparent";
                                  }}
                                  readOnly={hasOtherAmountEntries}
                                  title={
                                    hasOtherAmountEntries
                                      ? "This value is calculated from Other Amount entries"
                                      : ""
                                  }
                                />
                              </div>
                            ) : getColumnType(colIndex) === "huidNo" ? (
                              <div className="d-flex align-items-center position-relative">
                                <input
                                  ref={(el) =>
                                    (inputRefs.current[
                                      `${rowIndex}-${colIndex}`
                                    ] = el)
                                  }
                                  type="text"
                                  value={cell.value.replace(/##/g, ", ")}
                                  onChange={(e) => {
                                    handleInputChange(
                                      rowIndex,
                                      colIndex,
                                      e.target.value.replace(/, /g, "##")
                                    );
                                  }}
                                  onKeyDown={(e) => {
                                    handleKeyDown(
                                      e,
                                      rowIndex,
                                      colIndex,
                                      getColumnType(colIndex)
                                    );
                                    handleHuidKeyDown(e, rowIndex);
                                  }}
                                  onDoubleClick={(e) =>
                                    handleHuidInteraction(rowIndex, e)
                                  }
                                  className="form-control border-0 rounded-0"
                                  style={{
                                    fontSize: "0.85rem",
                                    padding: "0.4rem 0.5rem",
                                    backgroundColor: "transparent",
                                    width: "100%",
                                    transition: "background-color 0.2s",
                                    cursor: "pointer", // Indicate it's clickable
                                  }}
                                  onFocus={() =>
                                    handleFocus(rowIndex, colIndex)
                                  }
                                  onBlur={(e) => {
                                    e.target.style.backgroundColor =
                                      "transparent";
                                  }}
                                  autoComplete="off"
                                  title="Double-click or press Enter twice to edit details"
                                />
                                {
                                  // Removed `cell.value.includes("##") &&` condition to always show the info button
                                  <div className="position-relative">
                                    <Button
                                      ref={(el) =>
                                        (huidInfoBtnRefs.current[rowIndex] = el)
                                      }
                                      variant="link"
                                      className="p-0 px-1 text-primary"
                                      style={{
                                        fontSize: "1rem",
                                        position: "absolute",
                                        right: "5px",
                                        top: "50%",
                                        transform: "translateY(-50%)",
                                        zIndex: 1,
                                      }}
                                      onKeyDown={(e) =>
                                        handleHuidInfoKeyDown(e, rowIndex)
                                      }
                                      onClick={(e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        const tooltipId = `huid-tooltip-${rowIndex}`;
                                        const tooltip =
                                          tooltipRefs.current[tooltipId];

                                        if (tooltip) {
                                          // If tooltip is already visible, hide it
                                          if (
                                            tooltip.style.display === "block"
                                          ) {
                                            tooltip.style.display = "none";
                                            setActiveTooltip(null);

                                            // Clear any existing timeout
                                            if (tooltipTimeoutRef.current) {
                                              clearTimeout(
                                                tooltipTimeoutRef.current
                                              );
                                              tooltipTimeoutRef.current = null;
                                            }
                                            return;
                                          }

                                          // Hide any other visible tooltips first
                                          Object.entries(
                                            tooltipRefs.current
                                          ).forEach(([id, t]) => {
                                            if (t && id !== tooltipId) {
                                              t.style.display = "none";
                                            }
                                          });

                                          // Position and show tooltip
                                          positionTooltip(
                                            e.currentTarget,
                                            tooltip
                                          );

                                          // Set this as the active tooltip
                                          setActiveTooltip(tooltipId);

                                          // Set timeout to hide after 4 seconds
                                          if (tooltipTimeoutRef.current) {
                                            clearTimeout(
                                              tooltipTimeoutRef.current
                                            );
                                          }

                                          tooltipTimeoutRef.current =
                                            setTimeout(() => {
                                              if (tooltip) {
                                                tooltip.style.display = "none";
                                                setActiveTooltip(null);
                                                tooltipTimeoutRef.current =
                                                  null;
                                              }
                                            }, 4000);
                                        }
                                      }}
                                      onMouseEnter={(e) => {
                                        // Don't show on hover if already showing from a click
                                        if (tooltipTimeoutRef.current) return;

                                        const tooltipId = `huid-tooltip-${rowIndex}`;
                                        const tooltip =
                                          tooltipRefs.current[tooltipId];

                                        if (tooltip) {
                                          // Hide any other visible tooltips first
                                          Object.entries(
                                            tooltipRefs.current
                                          ).forEach(([id, t]) => {
                                            if (t && id !== tooltipId) {
                                              t.style.display = "none";
                                            }
                                          });

                                          // Position and show tooltip
                                          positionTooltip(
                                            e.currentTarget,
                                            tooltip
                                          );

                                          // Set this as the active tooltip
                                          setActiveTooltip(tooltipId);
                                        }
                                      }}
                                      onMouseLeave={(e) => {
                                        // Only hide if not in clicked mode
                                        if (tooltipTimeoutRef.current) return;

                                        const tooltipId = `huid-tooltip-${rowIndex}`;
                                        const currentTooltip =
                                          tooltipRefs.current[tooltipId];
                                        if (activeTooltip === tooltipId) {
                                          // Use setTimeout to allow moving to the tooltip
                                          setTimeout(() => {
                                            // Only hide if we're not hovering over the tooltip itself
                                            if (
                                              activeTooltip === tooltipId &&
                                              !currentTooltip?.matches(
                                                ":hover"
                                              ) &&
                                              !tooltipTimeoutRef.current
                                            ) {
                                              currentTooltip.style.display =
                                                "none";
                                              setActiveTooltip(null);
                                            }
                                          }, 100);
                                        }
                                      }}
                                    >
                                      <Icon icon="solar:info-circle-bold" />
                                    </Button>

                                    {/* HUID Details Tooltip */}
                                    <div
                                      ref={(el) =>
                                        (tooltipRefs.current[
                                          `huid-tooltip-${rowIndex}`
                                        ] = el)
                                      }
                                      className="shadow-lg rounded p-2 bg-white tooltip-container"
                                      style={{
                                        display: "none",
                                        width: "auto",
                                        maxWidth: "250px",
                                        zIndex: 9999,
                                        position: "fixed",
                                      }}
                                      onMouseEnter={() => {
                                        // Do nothing when hovering tooltip
                                        // This prevents the tooltip from disappearing
                                      }}
                                      onMouseLeave={(e) => {
                                        // Only hide if not in clicked mode
                                        if (tooltipTimeoutRef.current) return;

                                        const tooltipId = `huid-tooltip-${rowIndex}`;
                                        const currentTooltip =
                                          tooltipRefs.current[tooltipId];
                                        if (activeTooltip === tooltipId) {
                                          e.currentTarget.style.display =
                                            "none";
                                          setActiveTooltip(null);
                                        }
                                      }}
                                    >
                                      <div
                                        className="tooltip-arrow"
                                        style={{
                                          position: "absolute",
                                          width: "12px",
                                          height: "12px",
                                          backgroundColor: "white",
                                          border: "1px solid rgba(0,0,0,0.1)",
                                          transform: "rotate(45deg)",
                                          borderRight: "none",
                                          borderBottom: "none",
                                        }}
                                      />
                                      <div
                                        className="huid-details"
                                        style={{
                                          maxHeight: "200px",
                                          overflowY: "auto",
                                        }}
                                      >
                                        {cell.value
                                          .split("##")
                                          .map((huidItem, idx) => (
                                            <div
                                              key={idx}
                                              className="py-1"
                                              style={{
                                                borderBottom:
                                                  idx !==
                                                  cell.value.split("##")
                                                    .length -
                                                    1
                                                    ? "1px solid #eee"
                                                    : "none",
                                              }}
                                            >
                                              <span
                                                style={{ fontSize: "0.85rem" }}
                                              >
                                                {huidItem.trim()}
                                              </span>
                                            </div>
                                          ))}
                                      </div>
                                    </div>
                                  </div>
                                }
                              </div>
                            ) : (
                              <input
                                ref={(el) =>
                                  (inputRefs.current[
                                    `${rowIndex}-${colIndex}`
                                  ] = el)
                                }
                                type="text"
                                value={cell.value}
                                onChange={(e) => {
                                  const value = e.target.value;
                                  const columnType = COLUMNS[colIndex].id;

                                  // Special handling for design field - allow alphanumeric
                                  if (columnType === "design") {
                                    handleInputChange(
                                      rowIndex,
                                      colIndex,
                                      value
                                    );
                                  }
                                  // For numeric fields only allow numbers and decimal
                                  else if (
                                    value === "" ||
                                    /^\d*\.?\d*$/.test(value)
                                  ) {
                                    handleInputChange(
                                      rowIndex,
                                      colIndex,
                                      value
                                    );
                                  }
                                }}
                                className="form-control border-0 rounded-0"
                                style={{
                                  fontSize: "0.85rem",
                                  padding: "0.4rem 0.5rem",
                                  backgroundColor: "transparent",
                                  width: "100%",
                                  transition: "background-color 0.2s",
                                  cursor: "text",
                                }}
                                onKeyDown={(e) =>
                                  handleKeyDown(
                                    e,
                                    rowIndex,
                                    colIndex,
                                    COLUMNS[colIndex]?.id
                                  )
                                }
                                onFocus={() => handleFocus(rowIndex, colIndex)}
                                onBlur={(e) => {
                                  e.target.style.backgroundColor =
                                    "transparent";
                                }}
                              />
                            )}
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {canEditProduct && (
              <div className="text-end mt-3">
                <button
                  className={`btn btn-primary ${buttonClass}`}
                  onClick={() => handleSaveChanges()}
                >
                  Save
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
      {renderDropdown()}
      <OtherAmountModal
        otherAmountModal={otherAmountModal}
        setOtherAmountModal={setOtherAmountModal}
        cardClass={cardClass}
        otherAmountColumnWidths={otherAmountColumnWidths}
        handleOtherAmountColumnWidthChange={handleOtherAmountColumnWidthChange}
        suggestions={suggestions}
        setSuggestions={setSuggestions}
        getSuggestions={getSuggestions}
        applyChargeType={(entry, chargeName) =>
          applyOtherChargeToEntry(entry, chargeTypes, chargeName)
        }
        chargeOptions={chargeTypes}
        formatAmount={formatAmount}
        calculateAmount={calculateAmount}
        rateOnOptions={rateOnOptions}
        highlightedIndex={highlightedIndex}
        setHighlightedIndex={setHighlightedIndex}
        handleRateOnDropdown={handleRateOnDropdown}
        handleRateOnKeyDown={handleRateOnKeyDown}
        calculateLessWeight={calculateLessWeight}
        formatLessWeight={formatLessWeight}
        calculateTotal={calculateTotal}
        buttonClass={buttonClass}
        handleSaveOtherAmountEntries={handleSaveOtherAmountEntries}
        setTableData={setTableData}
      />
      {renderImageModal()}
      {renderSuccessModal()}
      {renderHuidModal()}
      {/* <p>Master Gold Rate: {masterGoldRate}</p> */}
    </div>
  );
};

export default EditingProduct;
